export type Lang = 'en' | 'ar';

type Dict = Record<string, string>;

const en: Dict = {
  'meta.home.title': 'IRIS | Iris Waste and Iris Vision',
  'meta.home.description':
    'IRIS is operations software with two lines: Iris Waste for fill-level collection, and Iris Vision for existing CCTV. English and Arabic. Request a demonstration on your site.',
  'og.home.title': 'IRIS: Iris Waste and Iris Vision',
  'og.home.description':
    'Two operating lines, one sign-in. Iris Waste reports fill on the site map. Iris Vision delivers alerts with snapshot evidence from cameras already on site.',
  'og.home.imageAlt': 'The Iris Waste application on a laptop, with street bins shown as fill rings on the site map.',
  'meta.waste.title': 'Iris Waste | Collect bins when they are full | IRIS',
  'meta.waste.description':
    'Iris Waste reports fill on a site map so teams collect the bins that need a visit. Free trial: IRIS installs one finished device on a bin you choose, for 2–4 weeks.',
  'og.waste.title': 'Iris Waste: collect bins when they are full',
  'og.waste.description': 'Fill rings on the site map. A finished device, installed by IRIS. Free trial on one bin for 2–4 weeks.',
  'og.waste.imageAlt':
    'The Iris Waste application on a laptop and a phone: bins as fill rings on the site map, and a street bin predicted to overflow before collection.',
  'meta.vision.title': 'Iris Vision | AI on cameras you already operate | IRIS',
  'meta.vision.description':
    'Iris Vision analyses existing CCTV and network cameras. Free demo: IRIS installs a local server on your site for 2–4 weeks. Camera feeds go to that server and stay with you.',
  'og.vision.title': 'Iris Vision: alerts with evidence from existing cameras',
  'og.vision.description': 'Free demo. IRIS installs a local server on your site. Camera feeds go to that server.',
  'og.vision.imageAlt': 'The Iris Vision alert inbox on a laptop, with a camera snapshot beside each alert.',
  'meta.demo.title': 'Request a demonstration | IRIS',
  'meta.demo.description':
    'Request an IRIS demonstration on your site. Both lines are free for 2–4 weeks. Iris Waste: a finished device on one bin, installed by IRIS. Iris Vision: a local server on your site; camera feeds stay with you.',
  'og.demo.title': 'Request an IRIS demonstration on your site',
  'og.demo.description': 'Free for 2–4 weeks. Iris Waste: a finished device on one bin. Iris Vision: a local server; camera feeds stay with you.',
  'og.demo.imageAlt': 'The IRIS demonstration request form.',
  'meta.privacy.title': 'Privacy | IRIS',
  'meta.privacy.description': 'How IRIS uses the details submitted on the demonstration form.',
  'og.privacy.title': 'IRIS privacy notice',
  'og.privacy.description': 'What the demonstration form collects, and how the IRIS team uses it.',
  'og.privacy.imageAlt': 'The IRIS wordmark.',
  'meta.terms.title': 'Terms | IRIS',
  'meta.terms.description': 'What requesting an IRIS demonstration means. Not a purchase contract.',
  'og.terms.title': 'IRIS terms',
  'og.terms.description': 'Requesting a demonstration is not a contract to buy. Waste trial and Vision demo as stated on this site.',
  'og.terms.imageAlt': 'The IRIS wordmark.',
  'jsonld.home':
    'IRIS is operations software with two lines: Iris Waste for fill-level collection, and Iris Vision for existing CCTV and network cameras.',
  'jsonld.waste':
    'Finished fill-level devices for bins, with a site map, alerts, reports and setup in the Iris Waste application. Installed by IRIS. Route planning and a crew application are available on request.',
  'jsonld.vision':
    'AI on existing CCTV and network cameras for retail, warehouses, campuses and public spaces. Alerts, counts, reports and snapshot evidence. Video is analysed on site.',
  'jsonld.waste.offer': 'Free trial: IRIS installs a finished device on one bin at a location the client chooses, for two to four weeks.',
  'jsonld.vision.offer':
    'Free demo: IRIS installs a local server on the client site for two to four weeks. Camera feeds go to that server and stay with the client. IRIS confirms details after a site review.',
  'jsonld.offer.name.waste': 'Free trial',
  'jsonld.offer.name.vision': 'Vision demo',

  skip: 'Skip to content',
  'brand.home': 'home',
  'nav.label': 'Primary',
  'nav.footer': 'Footer',
  'nav.waste': 'Waste',
  'nav.vision': 'Vision',
  'nav.privacy': 'Privacy',
  'nav.terms': 'Terms',
  'nav.cta': 'Request a demo',
  'nav.menu': 'Menu',
  'nav.close': 'Close menu',
  'nav.lang': 'Language',
  'nav.lang.en': 'EN',
  'nav.lang.ar': 'عربي',
  'nav.lang.switchToEn': 'Switch to English',
  'nav.lang.switchToAr': 'Switch to Arabic',
  'nav.page': 'On this page',
  'nav.software': 'Software',
  'nav.where': 'Where it is installed',
  'nav.examples': 'Examples',
  'nav.privacyData': 'Privacy and data',
  'nav.start': 'How you start',
  'nav.faq': 'FAQ',
  'line.waste': 'Iris Waste',
  'line.vision': 'Iris Vision',
  'soft.on_request': 'On request',
  'soft.sample': 'Sample data',
  'soft.optional': 'Optional',

  'home.eyebrow': 'IRIS',
  'home.title': 'Collect bins when they are full. Act on cameras you already operate.',
  'home.lead':
    'Iris Waste reports fill on a site map so collection visits the bins that need it. Iris Vision analyses existing CCTV on a local server and delivers alerts with snapshot evidence. One sign-in, in English and Arabic.',
  'home.cta': 'Request a demo',
  'home.lines': 'Review both lines',
  'home.note':
    'A free demonstration of either line, or both, for two to four weeks. Iris Waste: a finished device on one bin, installed by IRIS. Iris Vision: a local server on your site; camera feeds stay with you.',

  'lines.eyebrow': 'TWO LINES',
  'lines.title': 'Iris Waste and Iris Vision.',
  'lines.lead': 'Each line operates independently. Run both, and your team uses one sign-in.',
  'lines.together': 'One sign-in when both lines are in use.',
  'lines.waste.title': 'Collect bins when they are full.',
  'lines.waste.body':
    'Each bin appears on the site map as a fill ring. Supervisors select a bin for predicted overflow and the next collection. IRIS specifies and installs the finished device.',
  'lines.waste.1': 'Fill rings on the site map',
  'lines.waste.2': 'Alerts and reports in the Iris Waste application',
  'lines.waste.3': 'Finished devices, specified and installed by IRIS',
  'lines.waste.more': 'Iris Waste in detail',
  'lines.vision.title': 'Alerts with evidence, from cameras you already operate.',
  'lines.vision.body':
    'Existing CCTV and network cameras are analysed on a local server. Each alert arrives with a snapshot, the camera, and the time. Video is analysed on site.',
  'lines.vision.1': 'Uses cameras already installed',
  'lines.vision.2': 'Alerts with a snapshot as evidence',
  'lines.vision.3': 'Retail, warehouses, campuses and public spaces',
  'lines.vision.more': 'Iris Vision in detail',

  'why.eyebrow': 'WHY IRIS',
  'why.title': 'What operations teams receive.',
  'why.lead': 'One platform for both lines. IRIS specifies, installs, and runs the demonstration on your site.',
  'why.1': 'One sign-in for both lines',
  'why.1.d': 'Run Iris Waste, Iris Vision, or both. Your team authenticates once.',
  'why.2': 'Cameras already on site',
  'why.2.d': 'Iris Vision uses the CCTV and network cameras already installed. There is no camera replacement project.',
  'why.3': 'Finished devices, specified per bin',
  'why.3.d': 'Each Iris Waste device is an enclosed product. IRIS selects sensing, power, connection and mounting for that bin.',
  'why.4': 'Detections trained on your camera views',
  'why.4.d': 'The IRIS AI team can train detections for objects and events specific to your cameras. You review them before they are used.',
  'why.5': 'English and Arabic, including right-to-left',
  'why.5.d': 'This site and the applications are provided in both languages.',
  'why.6': 'Hosted by IRIS, or inside your organisation',
  'why.6.d': 'IRIS can host the service, or operate it where the system must remain with you. IRIS installs either arrangement.',

  'where.eyebrow': 'WHERE IT IS SPECIFIED',
  'where.title': 'Typical environments for each line.',
  'where.lead':
    'Iris Waste is specified for bins and refuse rooms. Iris Vision is specified for gates, floors, docks and public spaces, using cameras already installed.',
  'where.waste': 'Iris Waste environments',
  'where.vision': 'Iris Vision environments',
  'where.waste.residential_compound': 'Residential compounds: street bins and refuse rooms.',
  'where.waste.hotel': 'Hotels: kitchens, floors and service bins.',
  'where.waste.mall': 'Malls: concourses and refuse rooms.',
  'where.waste.hospital': 'Hospitals: wards, kitchens and service rooms.',
  'where.waste.office': 'Offices and campuses: floors and refuse rooms.',
  'where.waste.factory': 'Warehouses and yards: indoor rooms and outdoor bins.',
  'where.waste.city': 'Municipal streets and public bins.',
  'where.waste.other': 'Airports, ports and comparable sites.',
  'where.vision.residential_compound': 'Residential compounds: gates, lobbies and shared areas.',
  'where.vision.hotel': 'Hotels: lobbies, guest floors and service corridors.',
  'where.vision.mall': 'Malls: concourses, queues and doors.',
  'where.vision.hospital': 'Hospitals: entrances, waiting rooms and restricted doors.',
  'where.vision.office': 'Offices and campuses: doors, floors and parking.',
  'where.vision.factory': 'Warehouses: aisles, docks and safety zones.',
  'where.vision.city': 'Cities: plazas, corridors and transit stops.',
  'where.vision.other': 'Airports, ports and comparable public spaces.',

  'start.eyebrow': 'HOW YOU START',
  'start.title': 'A free demonstration on your site.',
  'start.lead': 'Two to four weeks. The IRIS team replies by email within 24 hours on weekdays.',
  'start.waste.title': 'Iris Waste: one bin',
  'start.waste.body': 'IRIS configures the software and installs a finished device on one bin at a location you choose.',
  'start.vision.title': 'Iris Vision: cameras already on site',
  'start.vision.body':
    'IRIS installs a local server on your site. Your camera feeds go to that server and stay with you. IRIS confirms details after a site review.',
  'start.k.does': 'IRIS',
  'start.k.need': 'You provide',
  'start.k.time': 'Duration',
  'start.waste.does': 'Installs one finished device and configures the software',
  'start.waste.need': 'One bin, and power and network near it',
  'start.waste.time': '2–4 weeks',
  'start.vision.does': 'Installs a local server. Camera feeds go to that server',
  'start.vision.need': 'Cameras already installed on site, and a site contact',
  'start.vision.time': '2–4 weeks',
  'start.waste.cta': 'Request a Waste demo',
  'start.vision.cta': 'Request an IRIS Vision demo',

  'host.eyebrow': 'WHERE IT RUNS',
  'host.title': 'Hosted by IRIS, or inside your organisation.',
  'host.cloud.title': 'IRIS hosts the service.',
  'host.cloud.body': 'Your team signs in. IRIS operates and maintains the service.',
  'host.own.title': 'Deployed inside your organisation.',
  'host.own.body': 'Where the system must remain with you, IRIS operates it there. The IRIS team configures that arrangement with you.',
  'sec.title': 'Access and records',
  'sec.1': 'People see only what their role allows.',
  'sec.2': 'Changes are recorded: who changed what.',
  'sec.3': 'For Iris Vision, camera feeds go to a server IRIS installs on your site. They never leave your servers.',
  'tools.title': 'Works with systems you already run.',
  'tools.body': 'Reports and alerts can reach the systems your team already uses, such as SAP, Oracle or Power BI.',

  'close.eyebrow': 'REQUEST A DEMO',
  'close.title': 'Run IRIS on your site.',
  'close.lead': 'Identify the site. The IRIS team replies by email within 24 hours on weekdays.',
  'close.waste.lead': 'Identify the site. The IRIS team replies by email within 24 hours on weekdays.',
  'close.vision.lead': 'Identify the site. The IRIS team replies by email within 24 hours on weekdays.',
  'close.cta': 'Request a demo',
  'close.waste.title': 'Run Iris Waste on one of your bins.',
  'close.vision.title': 'Run Iris Vision on cameras you already operate.',

  'waste.hero.eyebrow': 'IRIS WASTE',
  'waste.hero.title': 'Collect bins when they are full.',
  'waste.hero.lead':
    'The site map shows every bin as a fill ring. Select a bin for predicted overflow, the next collection, and fill history. IRIS installs a finished device on each bin.',
  'waste.hero.cta': 'Request a Waste demo',
  'waste.hero.note': 'Free trial: IRIS installs one finished device on a bin at a location you choose, for 2\u2060–\u20604\u00a0weeks.',
  'waste.hero.alt':
    'The Iris Waste application on a laptop and a phone: street bins as fill rings on the Garden Residences site map, beside a phone showing a street bin predicted to overflow before collection.',

  'wsoft.eyebrow': 'THE IRIS WASTE APPLICATION',
  'wsoft.title': 'The map, alerts, reports and setup your team uses.',
  'wsoft.lead':
    'Map, alerts, reports and setup are what you use in the demo. Route planning, the crew app, contractor times, carbon and diversion, a view across properties, and resident QR reports are available on request. They are not part of the demo.',
  'wsoft.map.tag': 'MAP',
  'wsoft.map.title': 'Every bin on the site map, as a fill ring.',
  'wsoft.map.body':
    'Supervisors see each bin on the site map, colour-coded by fill. Select a bin for predicted overflow before the next collection, and a fill history. People see only the sites their role allows. There is a record of who changed what.',
  'wsoft.map.1': 'Site map with fill rings per bin',
  'wsoft.map.2': 'Overflow before collection, flagged in advance',
  'wsoft.map.3': 'Fill history and predicted full time per bin',
  'wsoft.alerts.tag': 'ALERTS',
  'wsoft.alerts.title': 'One inbox for fill, fire, tilt and overflow.',
  'wsoft.alerts.body':
    'Alerts for fill, predicted overflow, fire, tilt and device health arrive in one inbox. An operator acknowledges ownership and resolves the alert when it is handled, including from a phone.',
  'wsoft.alerts.1': 'Fill, fire, tilt and overflow alerts',
  'wsoft.alerts.2': 'Acknowledge, then resolve',
  'wsoft.alerts.3': 'On a laptop or a phone',
  'wsoft.reports.tag': 'REPORTS',
  'wsoft.reports.title': 'Average fill at collection, per site.',
  'wsoft.reports.body':
    'Managers read average fill at collection and overflow risk for each site, on a laptop or a phone. Reports can go to the systems your team already uses.',
  'wsoft.reports.1': 'Average fill at collection, per site',
  'wsoft.reports.2': 'Daily dashboards and weekly reports',
  'wsoft.reports.3': 'Exports to systems you already use',
  'wsoft.setup.tag': 'SETUP',
  'wsoft.setup.title': 'Organisation, site, group and bin rules.',
  'wsoft.setup.body':
    'An administrator adds sites and bin groups, sets warning and critical levels once, and assigns who sees which sites. Groups inherit those rules. The installer brings each device onto the map from a phone, with a one-time activation code.',
  'wsoft.setup.1': 'Rules set once; groups and bins inherit them',
  'wsoft.setup.2': 'Roles limited to the assigned sites',
  'wsoft.setup.3': 'A one-time activation code at the bin',
  'wsoft.routes.tag': 'COLLECTION ORDER',
  'wsoft.routes.title': 'The day’s collection order, on the site map.',
  'wsoft.routes.body':
    'Available on request. IRIS can order stops by fill, predicted full time, truck capacity and shift length, drawn on the same site map. Not part of the demo.',
  'wsoft.routes.1': 'Stops ordered by need, on the map',
  'wsoft.routes.2': 'Bins that can wait are omitted',
  'wsoft.routes.3': 'Shown with sample data',
  'wsoft.crew.tag': 'CREW APPLICATION',
  'wsoft.crew.title': 'Today’s stops on the crew phone.',
  'wsoft.crew.body':
    'Available on request. Crews see today’s ordered stops, scan a QR code at the bin, and attach a photograph. Not part of the demo.',
  'wsoft.crew.1': 'Today’s stops, in order',
  'wsoft.crew.2': 'A QR scan at the bin',
  'wsoft.crew.3': 'Shown with sample data',

  'wopt.eyebrow': 'ON REQUEST',
  'wopt.title': 'Available on request.',
  'wopt.lead': 'Not part of the demo. Request them if the site requires them.',
  'wopt.sla': 'Contractor times, with evidence',
  'wopt.sla.d': 'How long a contractor takes to empty a critical bin, retained with timestamps and fill readings.',
  'wopt.carbon': 'Carbon and diversion',
  'wopt.carbon.d': 'Trips, distance and waste streams expressed as carbon and recycling figures for each site.',
  'wopt.portfolio': 'One view across properties',
  'wopt.portfolio.d': 'Compounds, hotels, stores and offices side by side, with a monthly pack.',
  'wopt.resident': 'Resident reports from a QR sticker',
  'wopt.resident.d': 'A QR sticker on the bin. The resident does not install an application.',

  'wwhy.eyebrow': 'WHY IRIS WASTE',
  'wwhy.title': 'Finished devices, specified and installed by IRIS.',
  'wwhy.lead': 'Each bin receives an enclosed product selected for that bin and that site.',
  'wwhy.1': 'A finished enclosure, specified per bin',
  'wwhy.2': 'Installed by the IRIS team',
  'wwhy.3': 'Sensing, power, connection and mounting selected per bin',
  'wwhy.4': 'Specified for indoor and outdoor use',
  'wwhy.5': 'The Iris Waste application in English and Arabic',
  'wwhy.6': 'Readings within 60 seconds of installation',
  'wwhy.7': 'Up to 5 years battery life on RF ultrasonic builds',

  'wapp.eyebrow': 'WHERE IT GOES',
  'wapp.title': 'IRIS selects the device for each kind of bin.',
  'wapp.lead': 'Every build is a finished enclosure. The IRIS team inspects the bin and what is near it, then selects the product.',
  'wapp.sku': 'Build code',
  'wapp.street': 'Compounds and street bins',
  'wapp.street.d': 'A device under the lid, on battery, reporting over the mobile network. Hotels and hospitals use the same line indoors.',
  'wapp.street.alt': 'Wheelie bins at a residential compound at dusk, with a finished IRIS device under the open lid of the nearest bin.',
  'wapp.indoor': 'Indoor refuse rooms',
  'wapp.indoor.d': 'A ceiling device on building power, using the building network. Specified for malls, hotels, hospitals and offices.',
  'wapp.indoor.alt': 'Green, blue and brown bins in an indoor refuse room, observed by a finished IRIS device on the ceiling.',
  'wapp.pole': 'Street stations',
  'wapp.pole.d': 'A solar device on a pole, using radio where cable will not reach.',
  'wapp.pole.alt': 'A solar-powered IRIS device on a street pole above a row of recycling bins on a city pavement.',
  'wapp.also': 'The same line covers warehouses, factories, airports, ports and industrial yards.',
  'choose.sensing': 'Sensing',
  'choose.sensing.v': 'Ultrasonic fill sensing, or a camera aimed at the bin',
  'choose.power': 'Power',
  'choose.power.v': 'Battery, mains, solar, or the network cable',
  'choose.conn': 'Connection',
  'choose.conn.v': '4G, Wi-Fi, Ethernet, or radio to an Iris Gateway',
  'choose.mount': 'Mounting',
  'choose.mount.v': 'On the bin, a wall, the ceiling, or a pole',

  'wstart.eyebrow': 'HOW YOU START',
  'wstart.title': 'A free trial, then a rollout if you continue.',
  'wstart.lead': 'IRIS installs one finished device. Your team uses the Iris Waste application on that bin for two to four weeks.',
  'wstart.alt': 'A finished IRIS device in a black enclosure, bolted to the inside wall of a blue steel skip.',
  'wstart.k.bins': 'Bins',
  'wstart.v.bins': 'One, at a location you choose',
  'wstart.k.hardware': 'Device',
  'wstart.v.hardware': 'A finished IRIS build, installed by IRIS',
  'wstart.k.software': 'Software',
  'wstart.v.software': 'Configured by IRIS for your site',
  'wstart.k.duration': 'Duration',
  'wstart.v.duration': '2–4 weeks',
  'wstart.how': 'The free trial.',
  'wstart.s1.when': 'Before installation',
  'wstart.s1.title': 'Select the bin.',
  'wstart.s1.body':
    'Identify the site, the bin to cover, and the power and network near it. The IRIS team selects the finished build that suits that bin.',
  'wstart.s2.when': 'Installation day',
  'wstart.s2.title': 'IRIS installs and configures.',
  'wstart.s2.body': 'The installer mounts the device, scans its QR code, and calibrates it in the application. The bin appears on your map.',
  'wstart.s3.when': 'During the trial',
  'wstart.s3.title': 'Your team operates IRIS.',
  'wstart.s3.body': 'Your team uses the map, alerts and history for that bin. After the trial, the hardware device is recovered.',
  'wstart.roll': 'If you continue: rollout.',
  'wstart.r1': 'Survey',
  'wstart.r1.d': 'Review the sites, select the right device for each bin, and plan the connection.',
  'wstart.r2': 'Configure',
  'wstart.r2.d': 'Load the bins, configure your organisation in IRIS, and connect your systems.',
  'wstart.r3': 'Install',
  'wstart.r3.d': 'Mount the devices, scan their QR codes, and calibrate them from the application.',
  'wstart.r4': 'Validate',
  'wstart.r4.d': 'Confirm that every bin is reporting before the team relies on it.',
  'wstart.r5': 'Operate',
  'wstart.r5.d': 'Daily dashboards, weekly reports, and a review with the IRIS team.',

  'wfaq.q1': 'What happens after the trial?',
  'wfaq.a1': 'The hardware device is recovered. You review the results with the IRIS team. Submitting the form is not a purchase.',
  'wfaq.q2': 'What do we provide?',
  'wfaq.a2': 'One bin at a location you choose, power and network near it, and a contact on site.',
  'wfaq.q3': 'Who installs the device?',
  'wfaq.a3': 'The IRIS team. The installer mounts the device, scans its QR code, and calibrates it in the application. Your team may observe.',
  'wfaq.q4': 'Which device do we receive?',
  'wfaq.a4':
    'One finished build that suits the bin. Readings within 60 seconds of installation. Up to 5 years battery life on RF ultrasonic builds. The IRIS team selects sensing, power, connection and mounting after inspecting the bin.',
  'wfaq.q5': 'Where does IRIS run?',
  'wfaq.a5': 'IRIS can host it for you, or it can run in your organisation. The IRIS team configures either arrangement with you.',
  'wfaq.q6': 'Does it work with our other systems?',
  'wfaq.a6': 'Reports and alerts can go to the systems your team already uses, such as SAP, Oracle or Power BI. The IRIS team configures this with you.',

  'vision.hero.eyebrow': 'IRIS VISION',
  'vision.hero.title': 'AI on cameras you already operate.',
  'vision.hero.lead':
    'The alert inbox shows each event with a camera snapshot, the zone, and the time. IRIS AI analyses existing CCTV on a local server. Your team acknowledges and resolves from a laptop or a phone.',
  'vision.hero.cta': 'Request an IRIS Vision demo',
  'vision.hero.note':
    'Free demo: IRIS installs a local server on your site for 2\u2060–\u20604\u00a0weeks. Your camera feeds go to that server and stay with you. IRIS confirms details after a site review.',
  'vision.hero.alt':
    'The Iris Vision application on a laptop and a phone: an alert inbox with camera snapshots, and an open alert on the phone showing a person in a forklift lane.',

  'vsoft.eyebrow': 'THE IRIS VISION APPLICATION',
  'vsoft.title': 'Alerts, cameras, counts, zones and reports.',
  'vsoft.lead':
    'Sample screens of the Iris Vision application. Each alert names the camera and the time, and retains a still of what the camera recorded.',
  'vsoft.alerts.tag': 'ALERT INBOX',
  'vsoft.alerts.title': 'Every alert arrives with a snapshot.',
  'vsoft.alerts.body':
    'Each detection opens with the snapshot that raised it, the camera, the zone and the time. The assigned person receives it on a laptop or a phone, acknowledges it, and resolves it when it is handled.',
  'vsoft.alerts.1': 'Snapshot evidence with every alert',
  'vsoft.alerts.2': 'Routed to the right team, by site and zone',
  'vsoft.alerts.3': 'Acknowledge and resolve from a phone',
  'vsoft.cameras.tag': 'CAMERAS',
  'vsoft.cameras.title': 'A still from each camera, with detections marked.',
  'vsoft.cameras.body':
    'A grid of your cameras shows a still from each one, with detections marked on the image. The grid shows stills, not a video wall.',
  'vsoft.cameras.1': 'Detections marked on each camera',
  'vsoft.cameras.2': 'Filter by site and zone',
  'vsoft.cameras.3': 'Online and offline cameras at a glance',
  'vsoft.counts.tag': 'COUNTS AND OCCUPANCY',
  'vsoft.counts.title': 'Footfall, dwell and occupancy, by hour.',
  'vsoft.counts.body':
    'The dashboard shows how many people entered, how long they remained in each zone, and occupancy for rooms, floors, docks and car parks. Set a limit, and IRIS notifies the assigned person when a zone exceeds it.',
  'vsoft.counts.1': 'Footfall in and out, by entrance',
  'vsoft.counts.2': 'Dwell by zone and time of day',
  'vsoft.counts.3': 'Occupancy limits with alerts',
  'vsoft.zones.tag': 'ZONES AND RULES',
  'vsoft.zones.title': 'Define zones on the camera view, then set the rules.',
  'vsoft.zones.body':
    'Draw safety zones, count lines and areas on the camera view. For each one, choose what IRIS should flag, during which hours, how urgent it is, and who is notified.',
  'vsoft.zones.1': 'Zones and count lines drawn on the view',
  'vsoft.zones.2': 'Rules for people, vehicles and PPE',
  'vsoft.zones.3': 'Hours, severity and who is notified',
  'vsoft.report.tag': 'DAILY REPORT',
  'vsoft.report.title': 'A daily report, and a weekly one.',
  'vsoft.report.body':
    'Each day IRIS summarises what the cameras flagged, how quickly the team responded, and the counts behind it, with stills of the events that warrant review. Reports are sent by email and open in Excel or Power BI.',
  'vsoft.report.1': 'Alerts by type, and response times',
  'vsoft.report.2': 'Stills of the events that warrant review',
  'vsoft.report.3': 'Exports to Excel, Power BI, SAP and Oracle',

  'vcustom.eyebrow': 'IRIS AI',
  'vcustom.title': 'Train IRIS on what matters on your site.',
  'vcustom.lead':
    'With IRIS AI you can train detections for custom objects, events and information specific to your site. The IRIS AI team trains them on your own camera views, and you review them before they are used.',
  'vcustom.ex': 'For example',
  'vcustom.ex1': 'A pallet left in front of a fire exit',
  'vcustom.ex2': 'A gate or roller door left open after hours',
  'vcustom.ex3': 'A vehicle type that should remain outside a zone',
  'vcustom.ex4': 'A product display left empty on the shop floor',
  'vcustom.s1': 'Describe what to detect',
  'vcustom.s1.d': 'Describe it in your own words, and which cameras see it.',
  'vcustom.s2': 'The IRIS AI team trains it',
  'vcustom.s2.d': 'The detection is trained on examples from your own cameras.',
  'vcustom.s3': 'You review it',
  'vcustom.s3.d': 'You inspect the first results. Its alerts then reach your team like any other.',
  'vcustom.cta': 'Request an IRIS Vision demo',

  'vapp.eyebrow': 'EXAMPLES',
  'vapp.title': 'Examples of what IRIS can detect.',
  'vapp.lead':
    'Four kinds of site, with examples for each. Hotels and hospitals use the same line, on cameras already installed. Your site can add its own detections.',
  'vapp.retail': 'Retail',
  'vapp.retail.d': 'Shop floors, entrances and checkouts.',
  'vapp.retail.1': 'Loss prevention',
  'vapp.retail.1.d': 'Entrances, exits and high-value aisles, watched for the events you choose.',
  'vapp.retail.2': 'Queue management',
  'vapp.retail.2.d': 'Checkout queues building, so an additional till can be opened.',
  'vapp.retail.3': 'Visitor analytics',
  'vapp.retail.3.d': 'Footfall and dwell by zone and time of day.',
  'vapp.retail.alt': 'A dome camera above a shop floor, with shoppers queueing at a counter.',
  'vapp.warehouse': 'Warehouses and logistics',
  'vapp.warehouse.d': 'Aisles, forklift routes and loading docks.',
  'vapp.warehouse.1': 'Safety zones',
  'vapp.warehouse.1.d': 'People entering forklift routes or restricted areas, flagged at once.',
  'vapp.warehouse.2': 'PPE',
  'vapp.warehouse.2.d': 'Missing helmets and vests, seen on camera.',
  'vapp.warehouse.3': 'Dock activity',
  'vapp.warehouse.3.d': 'Loading-bay occupancy and idle trailers, without inspecting the floor in person.',
  'vapp.warehouse.alt': 'A camera on a warehouse column above a worker in a helmet and vest, with a forklift in the aisle.',
  'vapp.campus': 'Corporate campuses',
  'vapp.campus.d': 'Doors, gates, floors and parking.',
  'vapp.campus.1': 'Access',
  'vapp.campus.1.d': 'Tailgating and after-hours entry at doors, gates and parking.',
  'vapp.campus.2': 'Occupancy',
  'vapp.campus.2.d': 'How floors, rooms and parking are actually used.',
  'vapp.campus.3': 'Perimeter',
  'vapp.campus.3.d': 'Fence lines and blind spots, watched without adding guard posts.',
  'vapp.campus.alt': 'A camera on a stone wall above a campus entrance with turnstiles, and parking beyond.',
  'vapp.city': 'Cities and public spaces',
  'vapp.city.d': 'Plazas, corridors and transit stops.',
  'vapp.city.1': 'Public-space awareness',
  'vapp.city.1.d': 'Plazas, corridors and transit stops, seen through existing city cameras.',
  'vapp.city.2': 'Crowding',
  'vapp.city.2.d': 'Crowds building at stops and in plazas, flagged before they become a problem.',
  'vapp.city.3': 'Faster response',
  'vapp.city.3.d': 'Alerts routed to the assigned team, instead of waiting for complaints.',
  'vapp.city.alt': 'A white dome camera on a grey pole at the edge of a stone plaza, with people waiting at a covered tram stop.',

  'vwhy.eyebrow': 'WHY IRIS VISION',
  'vwhy.title': 'AI on cameras you already operate.',
  'vwhy.lead': 'IRIS installs a local server on your site. Camera feeds stay with you. Custom detections can be trained on your camera views.',
  'vwhy.1': 'Uses cameras already installed',
  'vwhy.2': 'IRIS installs a local server on your site',
  'vwhy.3': 'Camera feeds stay on your servers',
  'vwhy.4': 'Detects people and faces; storing is customizable',
  'vwhy.5': 'Custom detections trained on your camera views',
  'vwhy.6': 'The Iris Vision application in English and Arabic',

  'vpriv.eyebrow': 'PRIVACY AND DATA',
  'vpriv.title': 'Where video remains.',
  'vpriv.lead': 'Camera feeds remain with you. After the demo, Vision data is deleted.',
  'vpriv.1': 'Where feeds go',
  'vpriv.1.d': 'IRIS installs a local server on your site. Your camera feeds go to that server. Data never leaves your servers.',
  'vpriv.2': 'People and faces',
  'vpriv.2.d': 'IRIS can detect people and faces; storing is customizable.',
  'vpriv.3': 'After the demo',
  'vpriv.3.d': 'Vision data is deleted. Data never leaves your servers.',
  'vpriv.4': 'Which cameras work',
  'vpriv.4.d': 'IRIS confirms which cameras can be used after a site review. This page does not list brands or a minimum count.',
  'vpriv.5': 'Who sees what',
  'vpriv.5.d': 'People see only what their role allows. Each alert can retain a still of what the camera recorded.',

  'vstart.eyebrow': 'HOW YOU START',
  'vstart.title': 'A free demonstration on cameras already on site.',
  'vstart.lead':
    'Request an IRIS Vision demo. IRIS installs a local server on your site for two to four weeks. Your camera feeds go to that server and stay with you. IRIS confirms details after a site review.',
  'vstart.alt': 'Two people on a site visit, one pointing at an existing camera above a loading door.',
  'vstart.k.cameras': 'Cameras',
  'vstart.v.cameras': 'Already installed on your site',
  'vstart.k.server': 'On site',
  'vstart.v.server': 'IRIS installs a local server. Camera feeds go to that server',
  'vstart.k.duration': 'Duration',
  'vstart.v.duration': '2–4 weeks, free',
  'vstart.how': 'The free demonstration.',
  'vstart.s1.when': 'Request',
  'vstart.s1.title': 'Identify the site.',
  'vstart.s1.body': 'Identify the site and the areas your cameras cover. IRIS replies by email within 24 hours on weekdays.',
  'vstart.s2.when': 'Site review',
  'vstart.s2.title': 'IRIS confirms the cameras and installs a server.',
  'vstart.s2.body': 'Which cameras work is confirmed after a site review. IRIS then installs a local server on your site. Your camera feeds go to that server.',
  'vstart.s3.when': '2–4 weeks',
  'vstart.s3.title': 'Your team operates IRIS.',
  'vstart.s3.body': 'Your team sees the alerts, counts and stills. After the demo, Vision data is deleted. Data never leaves your servers.',
  'vstart.roll': 'If you continue: rollout.',
  'vstart.r1': 'Survey',
  'vstart.r1.d': 'Review the sites and the cameras, and agree which areas and signals matter.',
  'vstart.r2': 'Configure',
  'vstart.r2.d': 'Draw zones and rules, assign who is notified, and connect your systems.',
  'vstart.r3': 'Connect',
  'vstart.r3.d': 'Camera feeds go to the local server IRIS installed on your site.',
  'vstart.r4': 'Validate',
  'vstart.r4.d': 'Review the first alerts and counts with your team before relying on them.',
  'vstart.r5': 'Operate',
  'vstart.r5.d': 'Daily dashboards, weekly reports, and a review with the IRIS team.',

  'vfaq.q1': 'Which cameras work?',
  'vfaq.a1': 'IRIS confirms after a site review. This page does not list brands, resolution or a minimum number of cameras.',
  'vfaq.q2': 'What is installed on site?',
  'vfaq.a2': 'A local server. Your camera feeds go to that server and stay with you. IRIS confirms details after a site review.',
  'vfaq.q3': 'Does IRIS detect people and faces?',
  'vfaq.a3': 'Yes. IRIS can detect people and faces; storing is customizable.',
  'vfaq.q4': 'Where is video kept, and for how long?',
  'vfaq.a4': 'Data never leaves your servers. After the demo, Vision data is deleted. Storing during the demo is customizable.',
  'vfaq.q5': 'Does it work with our other systems?',
  'vfaq.a5': 'Reports, counts and alerts can go to the systems your team already uses, such as SAP, Oracle or Power BI.',
  'vfaq.q6': 'What happens after the demo?',
  'vfaq.a6': 'Vision data is deleted. Data never leaves your servers. Submitting the form is not a purchase.',

  'demo.eyebrow': 'REQUEST A DEMO',
  'demo.title': 'Request a demonstration on your site.',
  'demo.lead':
    'Both lines are a free demonstration for two to four weeks. Select the line first. The form asks only what that line requires. There is no purchase obligation.',
  'demo.waste.body': 'IRIS installs a finished device on one bin at a location you choose. After the trial, the hardware is recovered.',
  'demo.vision.body': 'IRIS installs a local server on your site. Camera feeds go to that server and stay with you. After the demo, Vision data is deleted.',

  'cta.line': 'Which line should this demonstration cover?',
  'cta.line.waste': 'Iris Waste',
  'cta.line.vision': 'Iris Vision',
  'cta.line.both': 'Both',
  'cta.line.waste.d': 'One bin; a finished device installed by IRIS',
  'cta.line.vision.d': 'A local server; your camera feeds stay with you',
  'cta.line.both.d': 'A finished device on one bin, and your cameras',
  'cta.line.pick': 'Select a line above. The questions for that line then appear.',
  'cta.group.site': 'About you and the site',
  'cta.group.waste': 'About the bin',
  'cta.group.vision': 'About the cameras',
  'cta.company': 'Company',
  'cta.contact': 'Contact name',
  'cta.email': 'Work email',
  'cta.phone': 'Phone (optional)',
  'cta.siteType': 'Site type',
  'cta.site.choose': 'Choose a site type',
  'cta.placement': 'Where is the bin?',
  'cta.place.indoor': 'Indoor',
  'cta.place.outdoor': 'Outdoor',
  'cta.power': 'Power near the bin',
  'cta.power.unsure': 'Not sure',
  'cta.power.battery': 'None (battery)',
  'cta.power.mains': 'Mains socket',
  'cta.power.solar': 'Outdoor and sunny (solar)',
  'cta.power.poe': 'Network cable with power',
  'cta.conn': 'How can it connect?',
  'cta.conn.unsure': 'Not sure',
  'cta.conn.4g': '4G mobile signal',
  'cta.conn.wifi': 'Wi-Fi',
  'cta.conn.ethernet': 'Ethernet',
  'cta.conn.rf': 'None nearby (radio to an Iris Gateway)',
  'cta.hint.waste': 'IRIS installs one finished device on that bin and selects the build after inspecting it.',
  'cta.cameras': 'Are cameras already installed?',
  'cta.cameras.yes': 'Yes, in the areas of interest',
  'cta.cameras.some': 'In some of those areas',
  'cta.cameras.unsure': 'Not sure',
  'cta.hint.vision': 'IRIS installs a local server on your site. Camera feeds go to that server. Which cameras work is confirmed after a site review.',
  'cta.areas': 'Which areas, and what should IRIS detect there? (optional)',
  'cta.areas.ph': 'For example: queues at checkout, people in forklift routes, a door left open after hours…',
  'cta.notes': 'Notes',
  'cta.notesPh': 'Site address or city, and preferred visit days…',
  'cta.submit': 'Request a demo',
  'cta.sending': 'Sending…',
  'cta.ok': 'The request has been received. The IRIS team will reply to the work email you entered.',
  'cta.mailto': 'The form could not reach the server. Your mail application should have opened with the same details.',
  'cta.mailtoManual': 'Email the IRIS team instead',
  'cta.privacy': 'Sent to the IRIS team to follow up on this request. Not used as a public testimonial.',
  'cta.privacyLink': 'How we use this form',
  'cta.next': 'The IRIS team replies by email within 24 hours on weekdays. Email only. There is no purchase obligation.',
  'cta.hint.company': 'The organisation hosting the trial or demonstration.',
  'cta.hint.email': 'Use a work address. The IRIS team replies here.',
  'cta.hp': 'Leave this field empty',
  'cta.site.residential_compound': 'Residential compound',
  'cta.site.hotel': 'Hotel',
  'cta.site.mall': 'Mall or retail',
  'cta.site.hospital': 'Hospital',
  'cta.site.office': 'Office or campus',
  'cta.site.factory': 'Warehouse, factory or industrial yard',
  'cta.site.city': 'City district, streets or public spaces',
  'cta.site.other': 'Other (airport, port…)',

  'faq.eyebrow': 'FAQ',
  'faq.title': 'Before you request a demonstration',
  'faq.q1': 'Is the demonstration a free trial?',
  'faq.a1':
    'Yes, for both lines, for two to four weeks. Iris Waste: IRIS installs a finished device on one bin you choose. Iris Vision: IRIS installs a local server on your site; camera feeds go to that server and stay with you.',
  'faq.q2': 'Can we run both lines?',
  'faq.a2': 'Yes. Choose Both on the form. Your team uses one sign-in for both lines.',
  'faq.q3': 'Where does IRIS run?',
  'faq.a3': 'IRIS can host it for you, or it can run in your organisation. IRIS works in the EMEA region. The IRIS team configures either arrangement with you.',
  'faq.q4': 'Is Arabic supported?',
  'faq.a4': 'Yes. IRIS and this site are in English and Arabic, with right-to-left layout.',
  'faq.q5': 'Does IRIS work with our other systems?',
  'faq.a5': 'Reports, counts and alerts can go to the systems your team already uses, such as SAP, Oracle or Power BI.',
  'faq.q6': 'Are prices listed here?',
  'faq.a6': 'No. Request a demo. Pricing is discussed after the site is identified.',

  'legal.updated': 'This page describes the site as it works today. It is not a signed legal policy.',
  'privacy.eyebrow': 'PRIVACY',
  'privacy.title': 'How we use the demonstration form.',
  'privacy.p1':
    'The demo form collects company name, contact name, work email, optional phone, site type, which line this demonstration should cover, and notes about the bin or the cameras. You can email the IRIS team instead of using the form.',
  'privacy.p2':
    'The IRIS team uses those details only to follow up on the request. They are not used as a public testimonial.',
  'privacy.p3':
    'A language preference may be stored in your browser if you switch English and Arabic. The form itself does not set advertising cookies.',
  'privacy.p4':
    'Who the data controller is, how long the request is kept, and where it is stored, are not stated here. They need a signed privacy notice. Until then, treat this page as a description of the form.',
  'privacy.p5':
    'The IRIS team replies by email within 24 hours on weekdays, to the work address you enter. Email only. Questions about a request you already sent: use the same thread, or the sales address in the footer.',

  'terms.eyebrow': 'TERMS',
  'terms.title': 'What a demonstration request means.',
  'terms.p1': 'Submitting the form is a request to arrange a demonstration. It is not a contract to buy IRIS, and it does not set a price or a service level.',
  'terms.p2':
    'Iris Waste: a free trial means IRIS installs a finished device on one bin at a location you choose, for two to four weeks. After the trial, the hardware device is recovered.',
  'terms.p3':
    'Iris Vision: a free demo for two to four weeks. IRIS installs a local server on your site. Camera feeds go to that server. Data never leaves your servers. After the demo, Vision data is deleted. Which cameras work is confirmed after a site review.',
  'terms.p4':
    'Iris Waste map, alerts, reports and setup are what you use in the demo. Route planning, the crew app, contractor times, carbon and diversion, a view across properties, and resident QR reports are available on request. They are not part of the demo.',
  'terms.p5': 'Solace Technologies is the technology partner named in the footer. Customer-facing work on this site is done by IRIS.',

  'foot.place': '125 Banafseg, New Cairo, Egypt. EMEA.',
  'foot.sales': 'Sales',
  'foot.top': 'Back to top',
  'foot.partner': 'Technology partner: Solace Technologies',

  'shot.waste.operator-alerts.laptop':
    'Iris Waste alerts inbox with summary counts and a list of warnings for bins that will overflow before pickup, each with acknowledge and resolve actions.',
  'shot.waste.operator-bin.phone':
    'Iris Waste phone screen for a street bin in warning state, predicted full within hours, with the next pickup time and a fill history chart.',
  'shot.waste.executive-view.laptop':
    'Iris Waste reports with average fill at collection as the headline figure, plus bins monitored, overflows prevented, collections this week, and half-empty pickups.',
  'shot.waste.executive-view.phone':
    'Iris Waste reports on a phone: average fill at collection over the last 30 days against a 75% target, with bins monitored and half-empty pickups.',
  'shot.waste.installer-activation-code.phone':
    'Iris Waste install step on a phone with a one-time activation code, a 15-minute countdown, and a QR code to scan on the device.',
  'shot.waste.tenant-admin-groups.laptop':
    'Iris Waste groups page showing settings passing from organisation to site, group, and bin, with six bin groups and their warning, critical, and pickup rules.',
  'shot.waste.map.laptop':
    'Iris Waste site map of Garden Residences: street bins as fill rings on the map, a fleet summary, and an open panel for Olive Street Bin 4 predicted to overflow before the next pickup. Sample data.',
  'shot.waste.routes.laptop':
    'Iris Waste collection order page with today’s stops in order, each bin’s fill and arrival time, and the route drawn on a map. Sample data. Available on request.',
  'shot.waste.crew.phone':
    'Iris Waste crew app on a phone at stop 4 of 12: a lobby bin at 79% fill, a button to scan the bin’s QR code, and options to add a photo or report a problem. Sample data. Available on request.',
  'shot.vision.alerts.laptop':
    'Iris Vision alert inbox with sample alerts, each with a camera snapshot, and an open alert showing a person in a forklift lane marked on the snapshot.',
  'shot.vision.alert.phone':
    'Iris Vision alert on a phone: a person in a forklift route, with the snapshot, camera, zone and time, and buttons to acknowledge or assign.',
  'shot.vision.cameras.laptop':
    'Iris Vision camera grid with six snapshots and their detections marked: a store entrance count, a checkout queue, a blocked fire exit, a forklift lane, dock bays, and tailgating at lobby gates.',
  'shot.vision.counts.laptop':
    'Iris Vision counts and occupancy dashboard with sample footfall by hour, dwell by zone, occupancy bars per zone, and a store entrance with a count line.',
  'shot.vision.counts.phone':
    'Iris Vision counts on a phone: footfall so far, a queue alert at checkout, and occupancy by zone. Sample data.',
  'shot.vision.zones.laptop':
    'Iris Vision zone and rule setup: a forklift lane drawn on a warehouse camera view, with a rule for people in the lane, helmet and vest checks, severity, and who is notified.',
  'shot.vision.report.laptop':
    'Iris Vision daily report with sample alerts by type, response time, footfall and safety events, snapshots of the events that warrant review, and export buttons.',
};

const ar: Dict = {
  'meta.home.title': 'IRIS | \u2066Iris Waste\u2069 و \u2066Iris Vision\u2069',
  'meta.home.description':
    'IRIS برنامج تشغيل بخطّين: Iris Waste لجمع الحاويات حسب الامتلاء، و Iris Vision لكاميرات المراقبة الموجودة. بالعربية والإنجليزية. اطلبوا عرضاً تشغيلياً في موقعكم.',
  'og.home.title': 'IRIS: \u2066Iris Waste\u2069 و \u2066Iris Vision\u2069',
  'og.home.description':
    'خطّان تشغيليان، وتسجيل دخول واحد. Iris Waste يعرض الامتلاء على خريطة الموقع. Iris Vision يقدّم تنبيهات مع لقطة من الكاميرات المركّبة لديكم.',
  'og.home.imageAlt': 'تطبيق Iris Waste على حاسوب محمول، يُظهر حاويات الشارع كحلقات امتلاء على خريطة الموقع.',
  'meta.waste.title': 'Iris Waste | اجمعوا الحاويات عند امتلائها | IRIS',
  'meta.waste.description':
    'يعرض Iris Waste الامتلاء على خريطة الموقع فيُجمع الفريق الحاويات التي تحتاج إلى زيارة. التجربة المجانية: يركّب IRIS جهازاً مكتملاً على حاوية تختارونها، لمدة أسبوعين إلى أربعة أسابيع.',
  'og.waste.title': 'Iris Waste: اجمعوا الحاويات عندما تمتلئ',
  'og.waste.description': 'جهاز مكتمل على الحاوية يركّبه IRIS. تجربة مجانية على حاوية واحدة لمدة أسبوعين إلى أربعة أسابيع.',
  'og.waste.imageAlt': 'تطبيق Iris Waste على حاسوب محمول وهاتف: حاويات كحلقات امتلاء على خريطة الموقع، وحاوية شارع متوقع فيضانها قبل الجمع.',
  'meta.vision.title': 'Iris Vision | ذكاء اصطناعي على كاميراتكم الحالية | IRIS',
  'meta.vision.description':
    'يضيف Iris Vision الذكاء الاصطناعي إلى كاميرات المراقبة والشبكة الموجودة لديكم. عرض مجاني: يركّب IRIS خادماً محلياً في موقعكم لمدة أسبوعين إلى أربعة أسابيع. وتذهب تغذية الكاميرات إلى ذلك الخادم وتبقى عندكم.',
  'og.vision.title': 'Iris Vision: ذكاء اصطناعي على كاميراتكم الحالية',
  'og.vision.description': 'عرض مجاني. يركّب IRIS خادماً محلياً في موقعكم. وتذهب تغذية كاميراتكم إلى ذلك الخادم.',
  'og.vision.imageAlt': 'صندوق تنبيهات تطبيق Iris Vision، مع لقطة من الكاميرا بجانب كل تنبيه.',
  'meta.demo.title': 'اطلبوا عرضاً تجريبياً | IRIS',
  'meta.demo.description':
    'اطلبوا عرض IRIS في موقعكم. الخطّان مجانيان لمدة أسبوعين إلى أربعة أسابيع. Iris Waste: جهاز مكتمل على حاوية واحدة يركّبه IRIS. Iris Vision: خادم محلي في موقعكم؛ وتغذية الكاميرات تبقى عندكم.',
  'og.demo.title': 'اطلبوا عرض IRIS التجريبي في موقعكم',
  'og.demo.description': 'مجاني لمدة أسبوعين إلى أربعة أسابيع. Iris Waste: جهاز مكتمل على حاوية واحدة. Iris Vision: خادم محلي؛ وتغذية الكاميرات تبقى عندكم.',
  'og.demo.imageAlt': 'نموذج طلب عرض IRIS التجريبي.',
  'meta.privacy.title': 'الخصوصية | IRIS',
  'meta.privacy.description': 'كيف يستخدم IRIS البيانات التي ترسلونها في نموذج العرض التجريبي.',
  'og.privacy.title': 'إشعار خصوصية IRIS',
  'og.privacy.description': 'ما يجمعه نموذج العرض التجريبي، وكيف يستخدمه فريق IRIS.',
  'og.privacy.imageAlt': 'كلمة IRIS.',
  'meta.terms.title': 'الشروط | IRIS',
  'meta.terms.description': 'ماذا يعني طلب عرض IRIS تجريبي. ليس عقد شراء.',
  'og.terms.title': 'شروط IRIS',
  'og.terms.description': 'طلب العرض التجريبي ليس عقد شراء. تجربة Waste وعرض Vision كما هما مذكوران في هذا الموقع.',
  'og.terms.imageAlt': 'كلمة IRIS.',
  'jsonld.home': 'لـ IRIS خطّان: Iris Waste للحاويات والجمع، و Iris Vision للكاميرات الموجودة في الموقع.',
  'jsonld.waste':
    'أجهزة مكتملة لقياس امتلاء الحاويات، مع خريطة وتنبيهات وتقارير وإعداد في تطبيق Iris Waste. يركّبها IRIS. تخطيط المسارات وتطبيق الطاقم متاحان عند الطلب.',
  'jsonld.vision':
    'ذكاء اصطناعي على كاميرات المراقبة والشبكة الموجودة للتجزئة والمستودعات والمجمّعات المكتبية والأماكن العامة. تنبيهات وأعداد وتقارير ولقطات كدليل. يُحلَّل الفيديو داخل الموقع.',
  'jsonld.waste.offer': 'تجربة مجانية: يركّب IRIS جهازاً مكتملاً على حاوية واحدة في موقع يختاره العميل، لمدة أسبوعين إلى أربعة أسابيع.',
  'jsonld.vision.offer':
    'عرض مجاني: يركّب IRIS خادماً محلياً في موقع العميل لمدة أسبوعين إلى أربعة أسابيع. وتذهب تغذية الكاميرات إلى ذلك الخادم وتبقى عند العميل. ويؤكّد IRIS التفاصيل بعد معاينة الموقع.',
  'jsonld.offer.name.waste': 'تجربة مجانية',
  'jsonld.offer.name.vision': 'عرض Vision',

  skip: 'تخطّوا إلى المحتوى',
  'brand.home': 'الصفحة الرئيسية',
  'nav.label': 'الرئيسي',
  'nav.footer': 'التذييل',
  'nav.waste': 'Waste',
  'nav.vision': 'Vision',
  'nav.privacy': 'الخصوصية',
  'nav.terms': 'الشروط',
  'nav.cta': 'اطلبوا عرضاً تجريبياً',
  'nav.menu': 'القائمة',
  'nav.close': 'أغلقوا القائمة',
  'nav.lang': 'اللغة',
  'nav.lang.en': 'EN',
  'nav.lang.ar': 'عربي',
  'nav.lang.switchToEn': 'التبديل إلى الإنجليزية',
  'nav.lang.switchToAr': 'التبديل إلى العربية',
  'nav.page': 'في هذه الصفحة',
  'nav.software': 'البرنامج',
  'nav.where': 'أين يُركَّب',
  'nav.examples': 'أمثلة',
  'nav.privacyData': 'الخصوصية والبيانات',
  'nav.start': 'كيف تبدأون',
  'nav.faq': 'أسئلة',
  'line.waste': 'Iris Waste',
  'line.vision': 'Iris Vision',
  'soft.on_request': 'عند الطلب',
  'soft.sample': 'بيانات نموذجية',
  'soft.optional': 'اختياري',

  'home.eyebrow': 'IRIS',
  'home.title': 'اجمعوا الحاويات عند امتلائها. وتصرّفوا بناءً على الكاميرات التي تشغّلونها.',
  'home.lead':
    'يعرض Iris Waste الامتلاء على خريطة الموقع فيزور الجمع الحاويات التي تحتاجه. ويحلّل Iris Vision كاميرات المراقبة الموجودة على خادم محلي ويقدّم تنبيهات مع لقطة كدليل. تسجيل دخول واحد، بالعربية والإنجليزية.',
  'home.cta': 'اطلبوا عرضاً تجريبياً',
  'home.lines': 'راجعوا الخطّين',
  'home.note':
    'عرض تشغيلي مجاني لأي من الخطّين، أو كليهما، لمدة أسبوعين إلى أربعة أسابيع. Iris Waste: جهاز مكتمل على حاوية واحدة يركّبه IRIS. Iris Vision: خادم محلي في موقعكم؛ وتغذية الكاميرات تبقى عندكم.',

  'lines.eyebrow': 'خطّان',
  'lines.title': '\u2066Iris Waste\u2069 و \u2066Iris Vision\u2069.',
  'lines.lead': 'يعمل كل خط مستقلاً. وإذا شغّلتم الاثنين، يستخدم فريقكم تسجيلاً واحداً.',
  'lines.together': 'تسجيل دخول واحد عند تشغيل الخطّين.',
  'lines.waste.title': 'اجمعوا الحاويات عند امتلائها.',
  'lines.waste.body':
    'تظهر كل حاوية على خريطة الموقع كحلقة امتلاء. ويختار المشرفون حاوية لرؤية الفيضان المتوقع والجمع التالي. ويحدّد IRIS الجهاز المكتمل ويركّبه.',
  'lines.waste.1': 'حلقات امتلاء على خريطة الموقع',
  'lines.waste.2': 'تنبيهات وتقارير في تطبيق Iris Waste',
  'lines.waste.3': 'أجهزة مكتملة يحدّدها IRIS ويركّبها',
  'lines.waste.more': 'تفاصيل Iris Waste',
  'lines.vision.title': 'تنبيهات مع دليل، من الكاميرات التي تشغّلونها.',
  'lines.vision.body':
    'تُحلَّل كاميرات المراقبة والشبكة الموجودة على خادم محلي. ويصل كل تنبيه مع لقطة والكاميرا والوقت. ويُحلَّل الفيديو داخل الموقع.',
  'lines.vision.1': 'يستخدم الكاميرات المركّبة أصلاً',
  'lines.vision.2': 'تنبيهات مع لقطة كدليل',
  'lines.vision.3': 'التجزئة والمستودعات والمجمّعات المكتبية والأماكن العامة',
  'lines.vision.more': 'تفاصيل Iris Vision',

  'why.eyebrow': 'لماذا IRIS',
  'why.title': 'ما تتلقاه فرق التشغيل.',
  'why.lead': 'منصة واحدة للخطّين. يحدّد IRIS العرض التشغيلي ويركّبه ويديره في موقعكم.',
  'why.1': 'تسجيل دخول واحد للخطّين',
  'why.1.d': 'شغّلوا Iris Waste أو Iris Vision أو كليهما. يوثّق فريقكم الدخول مرة واحدة.',
  'why.2': 'الكاميرات المركّبة في الموقع',
  'why.2.d': 'يستخدم Iris Vision كاميرات المراقبة والشبكة الموجودة. ليس هناك مشروع استبدال كاميرات.',
  'why.3': 'أجهزة مكتملة تُحدَّد لكل حاوية',
  'why.3.d': 'كل جهاز Iris Waste منتج مغلق. يختار IRIS الاستشعار والطاقة والاتصال والتثبيت لتلك الحاوية.',
  'why.4': 'رصد مدرَّب على مناظر كاميراتكم',
  'why.4.d': 'يستطيع فريق IRIS للذكاء الاصطناعي تدريب رصد لأشياء وأحداث خاصة بكاميراتكم. وتراجعونه أنتم قبل استخدامه.',
  'why.5': 'العربية والإنجليزية، بما في ذلك من اليمين إلى اليسار',
  'why.5.d': 'هذا الموقع والتطبيقات باللغتين.',
  'why.6': 'يستضيفه IRIS، أو داخل مؤسستكم',
  'why.6.d': 'يمكن لـ IRIS استضافة الخدمة، أو تشغيلها حيث يجب أن يبقى النظام عندكم. وIRIS هو من يركّب أياً من الترتيبين.',

  'where.eyebrow': 'أين يُحدَّد',
  'where.title': 'بيئات نمطية لكل خط.',
  'where.lead':
    'يُحدَّد Iris Waste للحاويات وغرف النفايات. ويُحدَّد Iris Vision للبوابات والطوابق والأرصفة والأماكن العامة، باستخدام الكاميرات المركّبة أصلاً.',
  'where.waste': 'بيئات Iris Waste',
  'where.vision': 'بيئات Iris Vision',
  'where.waste.residential_compound': 'المجمّعات السكنية: حاويات الشارع وغرف النفايات.',
  'where.waste.hotel': 'الفنادق: المطابخ والطوابق وحاويات الخدمة.',
  'where.waste.mall': 'المراكز التجارية: الممرات وغرف النفايات.',
  'where.waste.hospital': 'المستشفيات: الأجنحة والمطابخ وغرف الخدمة.',
  'where.waste.office': 'المكاتب والمجمّعات: الطوابق وغرف النفايات.',
  'where.waste.factory': 'المستودعات والساحات: الغرف الداخلية والحاويات الخارجية.',
  'where.waste.city': 'الشوارع والحاويات العامة.',
  'where.waste.other': 'المطارات والموانئ والمواقع المشابهة.',
  'where.vision.residential_compound': 'المجمّعات السكنية: البوابات والردهات والمناطق المشتركة.',
  'where.vision.hotel': 'الفنادق: الردهات وطوابق النزلاء وممرات الخدمة.',
  'where.vision.mall': 'المراكز التجارية: الممرات والطوابير والأبواب.',
  'where.vision.hospital': 'المستشفيات: المداخل وغرف الانتظار والأبواب المقيّدة.',
  'where.vision.office': 'المكاتب والمجمّعات: الأبواب والطوابق ومواقف السيارات.',
  'where.vision.factory': 'المستودعات: الممرات وأرصفة التحميل ومناطق السلامة.',
  'where.vision.city': 'المدن: الساحات والممرات ومحطات النقل.',
  'where.vision.other': 'المطارات والموانئ والأماكن العامة المشابهة.',

  'start.eyebrow': 'كيف تبدأون',
  'start.title': 'عرض تشغيلي مجاني في موقعكم.',
  'start.lead': 'أسبوعان إلى أربعة أسابيع. ويردّ فريق IRIS بالبريد خلال 24 ساعة في أيام العمل.',
  'start.waste.title': 'Iris Waste: حاوية واحدة',
  'start.waste.body': 'يهيّئ IRIS البرنامج ويركّب جهازاً مكتملاً على حاوية واحدة في موقع تختارونه.',
  'start.vision.title': 'Iris Vision: الكاميرات المركّبة في الموقع',
  'start.vision.body': 'يركّب IRIS خادماً محلياً في موقعكم. وتذهب تغذية كاميراتكم إلى ذلك الخادم وتبقى عندكم. ويؤكّد IRIS التفاصيل بعد معاينة الموقع.',
  'start.k.does': 'IRIS',
  'start.k.need': 'توفّرون أنتم',
  'start.k.time': 'المدة',
  'start.waste.does': 'يركّب جهازاً مكتملاً واحداً ويهيّئ البرنامج',
  'start.waste.need': 'حاوية واحدة، والطاقة والشبكة قربها',
  'start.waste.time': 'أسبوعان إلى أربعة أسابيع',
  'start.vision.does': 'يركّب خادماً محلياً. وتذهب تغذية الكاميرات إلى ذلك الخادم',
  'start.vision.need': 'كاميرات مركّبة في الموقع، وجهة اتصال',
  'start.vision.time': 'أسبوعان إلى أربعة أسابيع',
  'start.waste.cta': 'اطلبوا عرض Waste',
  'start.vision.cta': 'اطلبوا عرض IRIS Vision',

  'host.eyebrow': 'أين يعمل النظام',
  'host.title': 'يستضيفه IRIS، أو داخل مؤسستكم.',
  'host.cloud.title': 'يستضيف IRIS الخدمة.',
  'host.cloud.body': 'يسجّل فريقكم الدخول، ويتولى IRIS تشغيل الخدمة وصيانتها.',
  'host.own.title': 'يُنشر داخل مؤسستكم.',
  'host.own.body': 'حيث يجب أن يبقى النظام عندكم، يشغّله IRIS هناك. ويجهّز فريق IRIS ذلك الترتيب معكم.',
  'sec.title': 'الوصول والسجلات',
  'sec.1': 'يرى كل شخص ما يسمح به دوره فقط.',
  'sec.2': 'تُسجَّل التغييرات: من غيّر ماذا.',
  'sec.3': 'بالنسبة إلى Iris Vision، تذهب تغذية الكاميرات إلى خادم يركّبه IRIS في موقعكم. ولا تغادر خوادمكم.',
  'tools.title': 'يعمل مع الأنظمة التي تشغّلونها.',
  'tools.body': 'يمكن أن تصل التقارير والتنبيهات إلى الأنظمة التي يشغّلها فريقكم، مثل SAP أو Oracle أو Power BI.',

  'close.eyebrow': 'اطلبوا عرضاً تجريبياً',
  'close.title': 'شغّلوا IRIS في موقعكم.',
  'close.lead': 'حدّدوا الموقع. ويردّ فريق IRIS بالبريد خلال 24 ساعة في أيام العمل.',
  'close.waste.lead': 'حدّدوا الموقع. ويردّ فريق IRIS بالبريد خلال 24 ساعة في أيام العمل.',
  'close.vision.lead': 'حدّدوا الموقع. ويردّ فريق IRIS بالبريد خلال 24 ساعة في أيام العمل.',
  'close.cta': 'اطلبوا عرضاً تجريبياً',
  'close.waste.title': 'شغّلوا Iris Waste على إحدى حاوياتكم.',
  'close.vision.title': 'شغّلوا Iris Vision على الكاميرات التي تشغّلونها.',

  'waste.hero.eyebrow': 'IRIS WASTE',
  'waste.hero.title': 'اجمعوا الحاويات عند امتلائها.',
  'waste.hero.lead':
    'تُظهر خريطة الموقع كل حاوية كحلقة امتلاء. اختاروا حاوية لرؤية الفيضان المتوقع والجمع التالي وسجل الامتلاء. يركّب IRIS جهازاً مكتملاً على كل حاوية.',
  'waste.hero.cta': 'اطلبوا عرض Waste',
  'waste.hero.note': 'تجربة مجانية: يركّب IRIS جهازاً مكتملاً على حاوية في موقع تختارونه، لمدة أسبوعين إلى أربعة أسابيع.',
  'waste.hero.alt':
    'تطبيق Iris Waste على حاسوب محمول وهاتف: حاويات على خريطة موقع Garden Residences مع حلقات امتلاء، بجانب هاتف يُظهر حاوية شارع ستفيض قبل الجمع.',

  'wsoft.eyebrow': 'تطبيق IRIS WASTE',
  'wsoft.title': 'الخريطة والتنبيهات والتقارير والإعداد التي يستخدمها فريقكم.',
  'wsoft.lead':
    'الخريطة والتنبيهات والتقارير والإعداد هي ما تستخدمونه في العرض. وتخطيط المسارات وتطبيق الطاقم وأزمنة المتعاقد والكربون والتحويل والعرض عبر العقارات وتقارير السكان عبر QR متاحة عند الطلب. وليست جزءاً من العرض.',
  'wsoft.map.tag': 'الخريطة',
  'wsoft.map.title': 'كل حاوية على خريطة الموقع، كحلقة امتلاء.',
  'wsoft.map.body':
    'يرى المشرفون كل حاوية على خريطة الموقع ملوّنة حسب الامتلاء. اختاروا حاوية لرؤية الفيضان المتوقع قبل الجمع التالي، وسجل الامتلاء. ويرى كل شخص المواقع التي يسمح بها دوره. وهناك سجل بمن غيّر ماذا.',
  'wsoft.map.1': 'خريطة الموقع مع حلقة امتلاء لكل حاوية',
  'wsoft.map.2': 'الفيضان قبل الجمع، يُنبَّه إليه مبكراً',
  'wsoft.map.3': 'سجل الامتلاء والوقت المتوقع للامتلاء لكل حاوية',
  'wsoft.alerts.tag': 'التنبيهات',
  'wsoft.alerts.title': 'صندوق واحد للامتلاء والحريق والميل والفيضان.',
  'wsoft.alerts.body':
    'تصل تنبيهات الامتلاء والفيضان المتوقع والحريق والميل وصحة الجهاز إلى صندوق واحد. يقرّ المشغّل بالملكية ويغلق التنبيه عند المعالجة، بما في ذلك من الهاتف.',
  'wsoft.alerts.1': 'تنبيهات الامتلاء والحريق والميل والفيضان',
  'wsoft.alerts.2': 'الإقرار ثم الإغلاق',
  'wsoft.alerts.3': 'من حاسوب محمول أو هاتف',
  'wsoft.reports.tag': 'التقارير',
  'wsoft.reports.title': 'متوسط الامتلاء عند الجمع، لكل موقع.',
  'wsoft.reports.body':
    'يقرأ المديرون متوسط الامتلاء عند الجمع وخطر الفيضان لكل موقع، على حاسوب محمول أو هاتف. ويمكن أن تصل التقارير إلى الأنظمة التي يستخدمها فريقكم.',
  'wsoft.reports.1': 'متوسط الامتلاء عند الجمع، لكل موقع',
  'wsoft.reports.2': 'لوحات يومية وتقارير أسبوعية',
  'wsoft.reports.3': 'تصدير إلى الأنظمة التي تستخدمونها',
  'wsoft.setup.tag': 'الإعداد',
  'wsoft.setup.title': 'قواعد المؤسسة والموقع والمجموعة والحاوية.',
  'wsoft.setup.body':
    'يضيف المسؤول المواقع ومجموعات الحاويات، ويضبط مستويات التحذير والحرج مرة واحدة، ويعيّن من يرى أي المواقع. وترث المجموعات تلك القواعد. ويُدخل المركِّب كل جهاز إلى الخريطة من هاتف، برمز تفعيل لمرة واحدة.',
  'wsoft.setup.1': 'تُضبط القواعد مرة؛ وترثها المجموعات والحاويات',
  'wsoft.setup.2': 'أدوار مقصورة على المواقع المعيَّنة',
  'wsoft.setup.3': 'رمز تفعيل لمرة واحدة عند الحاوية',
  'wsoft.routes.tag': 'ترتيب الجمع',
  'wsoft.routes.title': 'ترتيب جمع اليوم، على خريطة الموقع.',
  'wsoft.routes.body':
    'متاح عند الطلب. يمكن لـ IRIS ترتيب المحطات حسب الامتلاء والوقت المتوقع للامتلاء وسعة الشاحنة وطول الوردية، مرسومة على خريطة الموقع نفسها. وليس جزءاً من العرض.',
  'wsoft.routes.1': 'محطات مرتّبة حسب الحاجة، على الخريطة',
  'wsoft.routes.2': 'تُترك الحاويات التي يمكن أن تنتظر',
  'wsoft.routes.3': 'معروضة ببيانات نموذجية',
  'wsoft.crew.tag': 'تطبيق الطاقم',
  'wsoft.crew.title': 'محطات اليوم على هاتف الطاقم.',
  'wsoft.crew.body':
    'متاح عند الطلب. يمكن للطاقم رؤية محطات اليوم، ومسح رمز QR عند الحاوية، وإضافة صورة. وليس جزءاً من العرض.',
  'wsoft.crew.1': 'محطات اليوم، بالترتيب',
  'wsoft.crew.2': 'مسح QR عند الحاوية',
  'wsoft.crew.3': 'معروض ببيانات نموذجية',

  'wopt.eyebrow': 'عند الطلب',
  'wopt.title': 'متاح عند الطلب.',
  'wopt.lead': 'ليست جزءاً من العرض. اطلبوها إن احتاجها الموقع.',
  'wopt.sla': 'أزمنة المتعاقد، مع دليل',
  'wopt.sla.d': 'كم يستغرق المتعاقد لتفريغ حاوية حرجة، محفوظاً مع الأوقات وقراءات الامتلاء.',
  'wopt.carbon': 'الكربون والتحويل',
  'wopt.carbon.d': 'الرحلات والمسافة ومسارات النفايات تُحوَّل إلى أرقام كربون وإعادة تدوير لكل موقع.',
  'wopt.portfolio': 'عرض واحد عبر العقارات',
  'wopt.portfolio.d': 'مجمّعات وفنادق ومتاجر ومكاتب جنباً إلى جنب، مع حزمة شهرية.',
  'wopt.resident': 'تقارير السكان من ملصق QR',
  'wopt.resident.d': 'ملصق QR على الحاوية، بلا تطبيق يثبّته الساكن.',

  'wwhy.eyebrow': 'لماذا IRIS WASTE',
  'wwhy.title': 'أجهزة مكتملة، يحدّدها IRIS ويركّبها.',
  'wwhy.lead': 'تحصل كل حاوية على منتج مغلق يُختار لتلك الحاوية وذلك الموقع.',
  'wwhy.1': 'غلاف مكتمل يُحدَّد لكل حاوية',
  'wwhy.2': 'يركّبه فريق IRIS',
  'wwhy.3': 'الاستشعار والطاقة والاتصال والتثبيت تُختار لكل حاوية',
  'wwhy.4': 'يعمل في الداخل والخارج',
  'wwhy.5': 'تطبيق Iris Waste بالعربية والإنجليزية',
  'wwhy.6': 'قراءات خلال 60 ثانية من التركيب',
  'wwhy.7': 'عمر بطارية يصل إلى 5 سنوات على بناء RF فوق صوتي',

  'wapp.eyebrow': 'أين يُركَّب',
  'wapp.title': 'يختار IRIS الجهاز لكل نوع من الحاويات.',
  'wapp.lead': 'كل بناء غلاف مكتمل. يعاين فريق IRIS الحاوية وما حولها، ثم يختار المنتج.',
  'wapp.sku': 'رمز البناء',
  'wapp.street': 'المجمّعات وحاويات الشارع',
  'wapp.street.d': 'جهاز تحت الغطاء، على بطارية، يرسل عبر شبكة الجوال. وتستخدم الفنادق والمستشفيات الخط نفسه في الداخل.',
  'wapp.street.alt': 'حاويات بعجلات في مجمّع سكني عند الغسق، مع جهاز IRIS مكتمل تحت غطاء أقرب حاوية.',
  'wapp.indoor': 'غرف النفايات الداخلية',
  'wapp.indoor.d': 'جهاز سقف على طاقة المبنى، يستخدم شبكة المبنى. للمراكز التجارية والفنادق والمستشفيات والمكاتب.',
  'wapp.indoor.alt': 'حاويات خضراء وزرقاء وبنية في غرفة نفايات داخلية، يراقبها جهاز IRIS مكتمل على السقف.',
  'wapp.pole': 'محطات الشارع',
  'wapp.pole.d': 'جهاز شمسي على عمود، يستخدم الراديو حيث لا يصل الكابل.',
  'wapp.pole.alt': 'جهاز IRIS يعمل بالطاقة الشمسية على عمود شارع فوق صف من حاويات إعادة التدوير على رصيف مدينة.',
  'wapp.also': 'يغطي الخط نفسه المستودعات والمصانع والمطارات والموانئ والساحات الصناعية.',
  'choose.sensing': 'الاستشعار',
  'choose.sensing.v': 'استشعار امتلاء فوق صوتي، أو كاميرا موجّهة إلى الحاوية',
  'choose.power': 'الطاقة',
  'choose.power.v': 'بطارية، أو تيار المبنى، أو طاقة شمسية، أو كابل الشبكة',
  'choose.conn': 'الاتصال',
  'choose.conn.v': 'الجيل الرابع، أو Wi-Fi، أو Ethernet، أو راديو إلى بوابة Iris',
  'choose.mount': 'التثبيت',
  'choose.mount.v': 'على الحاوية، أو جدار، أو السقف، أو عمود',

  'wstart.eyebrow': 'كيف تبدأون',
  'wstart.title': 'تجربة مجانية، ثم طرح إذا تابعتم.',
  'wstart.lead': 'يركّب IRIS جهازاً مكتملاً واحداً. ويستخدم فريقكم تطبيق Iris Waste على تلك الحاوية لمدة أسبوعين إلى أربعة أسابيع.',
  'wstart.alt': 'جهاز IRIS مكتمل في غلاف أسود، مثبت بمسامير على الجدار الداخلي لحاوية فولاذية زرقاء.',
  'wstart.k.bins': 'الحاويات',
  'wstart.v.bins': 'واحدة، في موقع تختارونه',
  'wstart.k.hardware': 'الجهاز',
  'wstart.v.hardware': 'بناء IRIS مكتمل، يركّبه IRIS',
  'wstart.k.software': 'البرنامج',
  'wstart.v.software': 'يهيّئه IRIS لموقعكم',
  'wstart.k.duration': 'المدة',
  'wstart.v.duration': 'أسبوعان إلى أربعة أسابيع',
  'wstart.how': 'التجربة المجانية.',
  'wstart.s1.when': 'قبل التركيب',
  'wstart.s1.title': 'اختاروا الحاوية.',
  'wstart.s1.body': 'حدّدوا الموقع والحاوية المراد تغطيتها، والطاقة والشبكة قربها. ويختار فريق IRIS البناء المكتمل المناسب لتلك الحاوية.',
  'wstart.s2.when': 'يوم التركيب',
  'wstart.s2.title': 'يركّب IRIS ويهيّئ.',
  'wstart.s2.body': 'يثبّت المركِّب الجهاز، ويمسح رمز QR، ويعايره عبر التطبيق. وتظهر الحاوية على خريطتكم.',
  'wstart.s3.when': 'أثناء التجربة',
  'wstart.s3.title': 'يشغّل فريقكم IRIS.',
  'wstart.s3.body': 'يستخدم فريقكم الخريطة والتنبيهات والسجل لتلك الحاوية. وبعد التجربة يُستعاد الجهاز.',
  'wstart.roll': 'إذا تابعتم: الطرح.',
  'wstart.r1': 'المعاينة',
  'wstart.r1.d': 'مراجعة المواقع، واختيار الجهاز المناسب لكل حاوية، وتخطيط الاتصال.',
  'wstart.r2': 'التهيئة',
  'wstart.r2.d': 'تحميل الحاويات، وإعداد مؤسستكم في IRIS، وربط أنظمتكم.',
  'wstart.r3': 'التركيب',
  'wstart.r3.d': 'تثبيت الأجهزة، ومسح رموز QR، ومعايرتها من التطبيق.',
  'wstart.r4': 'التحقق',
  'wstart.r4.d': 'التحقق من أن كل حاوية تبلّغ قبل أن يعتمد الفريق عليها.',
  'wstart.r5': 'التشغيل',
  'wstart.r5.d': 'لوحات يومية وتقارير أسبوعية ومراجعة مع فريق IRIS.',

  'wfaq.q1': 'ماذا يحدث بعد التجربة؟',
  'wfaq.a1': 'يُستعاد الجهاز. وتراجعون النتائج مع فريق IRIS. وإرسال النموذج ليس شراءً.',
  'wfaq.q2': 'ماذا نوفّر؟',
  'wfaq.a2': 'حاوية واحدة في موقع تختارونه، والطاقة والشبكة قربها، وجهة اتصال في الموقع.',
  'wfaq.q3': 'من يركّب الجهاز؟',
  'wfaq.a3': 'فريق IRIS. يثبّت المركِّب الجهاز، ويمسح رمز QR، ويعايره عبر التطبيق. ويمكن لفريقكم أن يشاهد.',
  'wfaq.q4': 'أي جهاز نحصل عليه؟',
  'wfaq.a4':
    'بناء مكتمل واحد يناسب الحاوية. قراءات خلال 60 ثانية من التركيب. عمر بطارية يصل إلى 5 سنوات على بناء RF فوق صوتي. يختار فريق IRIS الاستشعار والطاقة والاتصال والتثبيت بعد معاينة الحاوية.',
  'wfaq.q5': 'أين يعمل IRIS؟',
  'wfaq.a5': 'يمكن لـ IRIS استضافته لكم، أو تشغيله داخل مؤسستكم. ويجهّز فريق IRIS أياً منهما معكم.',
  'wfaq.q6': 'هل يعمل مع أنظمتنا الأخرى؟',
  'wfaq.a6': 'يمكن أن تصل التقارير والتنبيهات إلى الأنظمة التي يستخدمها فريقكم، مثل SAP أو Oracle أو Power BI. ويجهّز فريق IRIS ذلك معكم.',

  'vision.hero.eyebrow': 'IRIS VISION',
  'vision.hero.title': 'ذكاء اصطناعي على الكاميرات التي تشغّلونها.',
  'vision.hero.lead':
    'يُظهر صندوق التنبيهات كل حدث مع لقطة من الكاميرا والمنطقة والوقت. يحلّل ذكاء IRIS الاصطناعي كاميرات المراقبة الموجودة على خادم محلي. ويقرّ فريقكم بالتنبيه ويغلقه من حاسوب محمول أو هاتف.',
  'vision.hero.cta': 'اطلبوا عرض IRIS Vision',
  'vision.hero.note':
    'عرض مجاني: يركّب IRIS خادماً محلياً في موقعكم لمدة أسبوعين إلى أربعة أسابيع. وتذهب تغذية كاميراتكم إلى ذلك الخادم وتبقى عندكم. ويؤكّد IRIS التفاصيل بعد معاينة الموقع.',
  'vision.hero.alt': 'تطبيق Iris Vision على حاسوب محمول وهاتف: صندوق تنبيهات مع لقطات كاميرا، وتنبيه مفتوح على الهاتف يُظهر شخصاً في مسار رافعة شوكية.',

  'vsoft.eyebrow': 'تطبيق IRIS VISION',
  'vsoft.title': 'التنبيهات والكاميرات والأعداد والمناطق والتقارير.',
  'vsoft.lead': 'شاشات نموذجية من تطبيق Iris Vision. يسمّي كل تنبيه الكاميرا والوقت، ويحتفظ بلقطة مما سجّلته الكاميرا.',
  'vsoft.alerts.tag': 'صندوق التنبيهات',
  'vsoft.alerts.title': 'يصل كل تنبيه مع لقطة.',
  'vsoft.alerts.body':
    'يُفتح كل رصد مع اللقطة التي أثارته، والكاميرا والمنطقة والوقت. يصل إلى الشخص المناسب على حاسوب محمول أو هاتف، فيقرّ به ويغلقه عند المعالجة.',
  'vsoft.alerts.1': 'دليل باللقطة مع كل تنبيه',
  'vsoft.alerts.2': 'يُرسل إلى الفريق المناسب، حسب الموقع والمنطقة',
  'vsoft.alerts.3': 'الإقرار والإغلاق من الهاتف',
  'vsoft.cameras.tag': 'الكاميرات',
  'vsoft.cameras.title': 'لقطة من كل كاميرا، مع الرصد معلَّماً عليها.',
  'vsoft.cameras.body': 'شبكة من كاميراتكم تعرض لقطة من كل واحدة، مع الرصد معلَّماً على الصورة. وتعرض الشبكة لقطات، لا جداراً من الفيديو.',
  'vsoft.cameras.1': 'الرصد معلَّم على كل كاميرا',
  'vsoft.cameras.2': 'تصفية حسب الموقع والمنطقة',
  'vsoft.cameras.3': 'الكاميرات المتصلة وغير المتصلة بنظرة واحدة',
  'vsoft.counts.tag': 'الأعداد والإشغال',
  'vsoft.counts.title': 'عدد الزوار ومدة البقاء والإشغال، حسب الساعة.',
  'vsoft.counts.body':
    'تعرض لوحة المعلومات كم دخل من الأشخاص، وكم بقوا في كل منطقة، وإشغال الغرف والطوابق والأرصفة ومواقف السيارات. اضبطوا حداً، ويُبلِّغ IRIS الشخص المعيَّن عندما تتجاوزه منطقة.',
  'vsoft.counts.1': 'الدخول والخروج، حسب المدخل',
  'vsoft.counts.2': 'مدة البقاء حسب المنطقة ووقت اليوم',
  'vsoft.counts.3': 'حدود إشغال مع تنبيهات',
  'vsoft.zones.tag': 'المناطق والقواعد',
  'vsoft.zones.title': 'حدّدوا المناطق على منظر الكاميرا، ثم اضبطوا القواعد.',
  'vsoft.zones.body':
    'ارسموا مناطق سلامة وخطوط عدّ ومساحات على منظر الكاميرا. ولكل واحدة، اختاروا ما يجب أن ينبّه إليه IRIS، وفي أي ساعات، ومدى الإلحاح، ومن يُبلَّغ.',
  'vsoft.zones.1': 'مناطق وخطوط عدّ مرسومة على المنظر',
  'vsoft.zones.2': 'قواعد للأشخاص والمركبات ومعدات الوقاية',
  'vsoft.zones.3': 'الساعات ودرجة الخطورة ومن يُبلَّغ',
  'vsoft.report.tag': 'التقرير اليومي',
  'vsoft.report.title': 'تقرير يومي، وآخر أسبوعي.',
  'vsoft.report.body':
    'يلخّص IRIS كل يوم ما نبّهت إليه الكاميرات، وسرعة استجابة الفريق، والأعداد وراء ذلك، مع لقطات للأحداث التي تستحق النظر. وتخرج التقارير بالبريد وتُفتح في Excel أو Power BI.',
  'vsoft.report.1': 'التنبيهات حسب النوع، وأزمنة الاستجابة',
  'vsoft.report.2': 'لقطات للأحداث التي تستحق النظر',
  'vsoft.report.3': 'تصدير إلى Excel وPower BI وSAP وOracle',

  'vcustom.eyebrow': 'ذكاء IRIS الاصطناعي',
  'vcustom.title': 'درّبوا IRIS على ما يهم في موقعكم.',
  'vcustom.lead':
    'مع ذكاء IRIS الاصطناعي يمكن تدريب رصد لأشياء وأحداث ومعلومات خاصة بموقعكم. يدرّبها فريق IRIS للذكاء الاصطناعي على مناظر كاميراتكم، وتراجعونها أنتم قبل أن تعمل.',
  'vcustom.ex': 'على سبيل المثال',
  'vcustom.ex1': 'منصة متروكة أمام مخرج طوارئ',
  'vcustom.ex2': 'بوابة أو باب رول بقي مفتوحاً بعد ساعات العمل',
  'vcustom.ex3': 'نوع مركبة يجب ألا يدخل منطقة',
  'vcustom.ex4': 'عرض منتجات تُرك فارغاً في أرض المتجر',
  'vcustom.s1': 'صفوا ما يجب رصده',
  'vcustom.s1.d': 'صفوه بكلماتكم، وأي الكاميرات تراه.',
  'vcustom.s2': 'يدرّبه فريق IRIS للذكاء الاصطناعي',
  'vcustom.s2.d': 'يُدرَّب الرصد على أمثلة من كاميراتكم.',
  'vcustom.s3': 'تراجعونه أنتم',
  'vcustom.s3.d': 'تراجعون النتائج الأولى. ثم تصل تنبيهاته إلى فريقكم كأي تنبيه آخر.',
  'vcustom.cta': 'اطلبوا عرض IRIS Vision',

  'vapp.eyebrow': 'أمثلة',
  'vapp.title': 'أمثلة لما يمكن أن يرصده IRIS.',
  'vapp.lead':
    'أربعة أنواع من المواقع، مع أمثلة لكل منها. وتستخدم الفنادق والمستشفيات الخط نفسه، على الكاميرات الموجودة هناك. ويمكن لموقعكم إضافة رصده الخاص.',
  'vapp.retail': 'التجزئة',
  'vapp.retail.d': 'أرضيات المتاجر والمداخل ونقاط المحاسبة.',
  'vapp.retail.1': 'منع الخسارة',
  'vapp.retail.1.d': 'المداخل والمخارج وممرات السلع الثمينة، تُراقب للأحداث التي تختارونها.',
  'vapp.retail.2': 'إدارة الطوابير',
  'vapp.retail.2.d': 'طوابير المحاسبة وهي تطول، حتى يُفتح صندوق إضافي.',
  'vapp.retail.3': 'تحليل الزوار',
  'vapp.retail.3.d': 'عدد الزوار ومدة البقاء حسب المنطقة ووقت اليوم.',
  'vapp.retail.alt': 'كاميرا قبة فوق أرض متجر، والمتسوقون في طابور عند منضدة.',
  'vapp.warehouse': 'المستودعات والخدمات اللوجستية',
  'vapp.warehouse.d': 'الممرات ومسارات الرافعات الشوكية وأرصفة التحميل.',
  'vapp.warehouse.1': 'مناطق السلامة',
  'vapp.warehouse.1.d': 'أشخاص يدخلون مسارات الرافعات الشوكية أو مناطق مقيّدة، يُنبَّه إليهم فوراً.',
  'vapp.warehouse.2': 'معدات الوقاية',
  'vapp.warehouse.2.d': 'خوذات وسترات ناقصة، تُرى على الكاميرا.',
  'vapp.warehouse.3': 'نشاط الرصيف',
  'vapp.warehouse.3.d': 'إشغال أرصفة التحميل والمقطورات المتوقفة، دون معاينة الأرض ميدانياً.',
  'vapp.warehouse.alt': 'كاميرا على عمود مستودع فوق عامل بخوذة وسترة، مع رافعة شوكية في الممر.',
  'vapp.campus': 'المجمّعات المكتبية',
  'vapp.campus.d': 'الأبواب والبوابات والطوابق ومواقف السيارات.',
  'vapp.campus.1': 'الدخول',
  'vapp.campus.1.d': 'الدخول خلف شخص آخر والدخول بعد ساعات العمل عند الأبواب والبوابات والمواقف.',
  'vapp.campus.2': 'الإشغال',
  'vapp.campus.2.d': 'كيف تُستخدم الطوابق والغرف والمواقف فعلاً.',
  'vapp.campus.3': 'المحيط',
  'vapp.campus.3.d': 'خطوط السياج والنقاط العمياء، تُراقب دون إضافة نقاط حراسة.',
  'vapp.campus.alt': 'كاميرا على جدار حجري فوق مدخل مجمّع ببوابات دوارة، ومواقف سيارات في الخلف.',
  'vapp.city': 'المدن والأماكن العامة',
  'vapp.city.d': 'الساحات والممرات ومحطات النقل.',
  'vapp.city.1': 'وعي المكان العام',
  'vapp.city.1.d': 'الساحات والممرات ومحطات النقل، تُرى عبر كاميرات المدينة الموجودة.',
  'vapp.city.2': 'التزاحم',
  'vapp.city.2.d': 'حشود تتكوّن عند المحطات وفي الساحات، يُنبَّه إليها قبل أن تصبح مشكلة.',
  'vapp.city.3': 'استجابة أسرع',
  'vapp.city.3.d': 'تنبيهات تُوجَّه إلى الفريق المناسب، بدل انتظار الشكاوى.',
  'vapp.city.alt': 'كاميرا قبة بيضاء على عمود رمادي عند حافة ساحة حجرية، وأناس ينتظرون عند محطة ترام مغطاة.',

  'vwhy.eyebrow': 'لماذا IRIS VISION',
  'vwhy.title': 'ذكاء اصطناعي على الكاميرات التي تشغّلونها أصلاً.',
  'vwhy.lead': 'يركّب IRIS خادماً محلياً في موقعكم. وتغذية الكاميرات تبقى عندكم. ويمكن تدريب رصد مخصص على مناظر كاميراتكم.',
  'vwhy.1': 'يستخدم الكاميرات الموجودة لديكم',
  'vwhy.2': 'يركّب IRIS خادماً محلياً في موقعكم',
  'vwhy.3': 'تغذية الكاميرات تبقى على خوادمكم',
  'vwhy.4': 'يرصد الأشخاص والوجوه؛ والحفظ قابل للتخصيص',
  'vwhy.5': 'رصد مخصص مدرَّب على مناظر كاميراتكم',
  'vwhy.6': 'تطبيق Iris Vision بالعربية والإنجليزية',

  'vpriv.eyebrow': 'الخصوصية والبيانات',
  'vpriv.title': 'أين يبقى الفيديو.',
  'vpriv.lead': 'تغذية الكاميرات تبقى عندكم. وبعد العرض تُحذف بيانات Vision.',
  'vpriv.1': 'إلى أين تذهب التغذية',
  'vpriv.1.d': 'يركّب IRIS خادماً محلياً في موقعكم. وتذهب تغذية كاميراتكم إلى ذلك الخادم. ولا تغادر البيانات خوادمكم.',
  'vpriv.2': 'الأشخاص والوجوه',
  'vpriv.2.d': 'يمكن لـ IRIS رصد الأشخاص والوجوه. وكيفية حفظ الرصد قابلة للتخصيص.',
  'vpriv.3': 'بعد العرض',
  'vpriv.3.d': 'تُحذف بيانات Vision. ولا تغادر البيانات خوادمكم.',
  'vpriv.4': 'أي كاميرات تعمل',
  'vpriv.4.d': 'يؤكّد IRIS أي الكاميرات يمكن استخدامها بعد معاينة الموقع. ولا تذكر هذه الصفحة علامات تجارية أو حداً أدنى للعدد.',
  'vpriv.5': 'من يرى ماذا',
  'vpriv.5.d': 'يرى كل شخص ما يسمح به عمله فقط. ويمكن لكل تنبيه أن يحتفظ بلقطة مما رأته الكاميرا.',

  'vstart.eyebrow': 'كيف تبدأون',
  'vstart.title': 'عرض تشغيلي مجاني على الكاميرات المركّبة في الموقع.',
  'vstart.lead':
    'اطلبوا عرض IRIS Vision. يركّب IRIS خادماً محلياً في موقعكم لمدة أسبوعين إلى أربعة أسابيع. وتذهب تغذية كاميراتكم إلى ذلك الخادم وتبقى عندكم. ويؤكّد IRIS التفاصيل بعد معاينة الموقع.',
  'vstart.alt': 'شخصان في زيارة موقع، أحدهما يشير إلى كاميرا موجودة فوق باب تحميل.',
  'vstart.k.cameras': 'الكاميرات',
  'vstart.v.cameras': 'مركّبة أصلاً في موقعكم',
  'vstart.k.server': 'في الموقع',
  'vstart.v.server': 'يركّب IRIS خادماً محلياً. وتذهب تغذية الكاميرات إلى ذلك الخادم',
  'vstart.k.duration': 'المدة',
  'vstart.v.duration': 'أسبوعان إلى أربعة أسابيع، مجاناً',
  'vstart.how': 'العرض المجاني.',
  'vstart.s1.when': 'الطلب',
  'vstart.s1.title': 'حدّدوا الموقع.',
  'vstart.s1.body': 'حدّدوا الموقع والمناطق التي تغطيها كاميراتكم. ويردّ IRIS بالبريد خلال 24 ساعة في أيام العمل.',
  'vstart.s2.when': 'معاينة الموقع',
  'vstart.s2.title': 'يؤكّد IRIS الكاميرات ويركّب خادماً.',
  'vstart.s2.body': 'أي الكاميرات تعمل يُؤكَّد بعد معاينة الموقع. ثم يركّب IRIS خادماً محلياً في موقعكم. وتذهب تغذية كاميراتكم إلى ذلك الخادم.',
  'vstart.s3.when': 'أسبوعان إلى أربعة',
  'vstart.s3.title': 'يشغّل فريقكم IRIS.',
  'vstart.s3.body': 'يرى فريقكم التنبيهات والأعداد واللقطات. وبعد العرض تُحذف بيانات Vision. ولا تغادر البيانات خوادمكم.',
  'vstart.roll': 'إذا تابعتم: الطرح.',
  'vstart.r1': 'المعاينة',
  'vstart.r1.d': 'مراجعة المواقع والكاميرات، والاتفاق على المناطق والإشارات المهمة.',
  'vstart.r2': 'التهيئة',
  'vstart.r2.d': 'رسم المناطق والقواعد، وتحديد من يُبلَّغ بماذا، وربط أنظمتكم.',
  'vstart.r3': 'التوصيل',
  'vstart.r3.d': 'تذهب تغذية الكاميرات إلى الخادم المحلي الذي ركّبه IRIS في موقعكم.',
  'vstart.r4': 'التحقق',
  'vstart.r4.d': 'التحقق من التنبيهات والأعداد الأولى مع فريقكم قبل الاعتماد عليها.',
  'vstart.r5': 'التشغيل',
  'vstart.r5.d': 'لوحات يومية وتقارير أسبوعية ومراجعة مع فريق IRIS.',

  'vfaq.q1': 'أي كاميرات تعمل؟',
  'vfaq.a1': 'يؤكّد IRIS ذلك بعد معاينة الموقع. ولا تذكر هذه الصفحة علامات تجارية أو دقة أو حداً أدنى لعدد الكاميرات.',
  'vfaq.q2': 'ماذا يُركَّب في الموقع؟',
  'vfaq.a2': 'خادم محلي. وتذهب تغذية كاميراتكم إلى ذلك الخادم وتبقى عندكم. ويؤكّد IRIS التفاصيل بعد معاينة الموقع.',
  'vfaq.q3': 'هل يرصد IRIS الأشخاص والوجوه؟',
  'vfaq.a3': 'نعم. يمكن لـ IRIS رصد الأشخاص والوجوه. وكيفية حفظ الرصد قابلة للتخصيص.',
  'vfaq.q4': 'أين يُحفظ الفيديو، ولأي مدة؟',
  'vfaq.a4': 'لا تغادر البيانات خوادمكم. وبعد العرض تُحذف بيانات Vision. والحفظ أثناء العرض قابل للتخصيص.',
  'vfaq.q5': 'هل يعمل مع أنظمتنا الأخرى؟',
  'vfaq.a5': 'يمكن أن تصل التقارير والأعداد والتنبيهات إلى الأنظمة التي يستخدمها فريقكم، مثل SAP أو Oracle أو Power BI.',
  'vfaq.q6': 'ماذا يحدث بعد العرض؟',
  'vfaq.a6': 'تُحذف بيانات Vision. ولا تغادر البيانات خوادمكم. وإرسال النموذج ليس شراءً.',

  'demo.eyebrow': 'اطلبوا عرضاً تجريبياً',
  'demo.title': 'اطلبوا عرضاً تشغيلياً في موقعكم.',
  'demo.lead': 'الخطّان عرض مجاني لمدة أسبوعين إلى أربعة أسابيع. اختاروا الخط أولاً. ويسأل النموذج فقط عما يحتاجه ذلك الخط. وليس هناك التزام بالشراء.',
  'demo.waste.body': 'يركّب IRIS جهازاً مكتملاً على حاوية واحدة في موقع تختارونه. وبعد التجربة يُستعاد الجهاز.',
  'demo.vision.body': 'يركّب IRIS خادماً محلياً في موقعكم. وتذهب تغذية الكاميرات إلى ذلك الخادم وتبقى عندكم. وبعد العرض تُحذف بيانات Vision.',

  'cta.line': 'أي خط يجب أن يغطيه هذا العرض؟',
  'cta.line.waste': 'Iris Waste',
  'cta.line.vision': 'Iris Vision',
  'cta.line.both': 'كلاهما',
  'cta.line.waste.d': 'حاوية واحدة؛ جهاز مكتمل يركّبه IRIS',
  'cta.line.vision.d': 'خادم محلي؛ وتغذية الكاميرات تبقى عندكم',
  'cta.line.both.d': 'جهاز مكتمل على حاوية واحدة، وكاميراتكم',
  'cta.line.pick': 'اختاروا خطاً أعلاه. ثم تظهر أسئلة ذلك الخط هنا.',
  'cta.group.site': 'عنكم وعن الموقع',
  'cta.group.waste': 'عن الحاوية',
  'cta.group.vision': 'عن الكاميرات',
  'cta.company': 'الشركة',
  'cta.contact': 'اسم جهة الاتصال',
  'cta.email': 'البريد المهني',
  'cta.phone': 'الهاتف (اختياري)',
  'cta.siteType': 'نوع الموقع',
  'cta.site.choose': 'اختاروا نوع الموقع',
  'cta.placement': 'أين الحاوية؟',
  'cta.place.indoor': 'داخلي',
  'cta.place.outdoor': 'خارجي',
  'cta.power': 'الطاقة قرب الحاوية',
  'cta.power.unsure': 'غير متأكدين',
  'cta.power.battery': 'لا يوجد (بطارية)',
  'cta.power.mains': 'مقبس تيار',
  'cta.power.solar': 'خارجي ومشمس (شمسي)',
  'cta.power.poe': 'كابل شبكة مع طاقة',
  'cta.conn': 'كيف يمكنه الاتصال؟',
  'cta.conn.unsure': 'غير متأكدين',
  'cta.conn.4g': 'إشارة الجيل الرابع',
  'cta.conn.wifi': 'Wi-Fi',
  'cta.conn.ethernet': 'Ethernet',
  'cta.conn.rf': 'لا يوجد قريب (راديو إلى بوابة Iris)',
  'cta.hint.waste': 'يركّب IRIS جهازاً مكتملاً واحداً على تلك الحاوية ويختار البناء بعد معاينتها.',
  'cta.cameras': 'هل الكاميرات مركّبة أصلاً؟',
  'cta.cameras.yes': 'نعم، في مناطق الاهتمام',
  'cta.cameras.some': 'في بعض تلك المناطق',
  'cta.cameras.unsure': 'غير متأكدين',
  'cta.hint.vision': 'يركّب IRIS خادماً محلياً في موقعكم. وتذهب تغذية الكاميرات إلى ذلك الخادم. وأي الكاميرات تعمل يُؤكَّد بعد معاينة الموقع.',
  'cta.areas': 'أي مناطق، وماذا يجب أن يرصد IRIS هناك؟ (اختياري)',
  'cta.areas.ph': 'مثلاً: طوابير عند المحاسبة، أشخاص في مسارات الرافعات الشوكية، باب بقي مفتوحاً بعد ساعات العمل…',
  'cta.notes': 'ملاحظات',
  'cta.notesPh': 'عنوان الموقع أو المدينة، وأيام الزيارة المفضّلة…',
  'cta.submit': 'اطلبوا عرضاً تجريبياً',
  'cta.sending': 'جارٍ الإرسال…',
  'cta.ok': 'وصل الطلب. وسيردّ فريق IRIS على البريد المهني الذي أدخلتموه.',
  'cta.mailto': 'تعذّر وصول النموذج إلى الخادم. يفترض أن يكون تطبيق البريد قد فتح بالتفاصيل نفسها.',
  'cta.mailtoManual': 'راسلوا فريق IRIS بالبريد بدلاً من ذلك',
  'cta.privacy': 'يُرسل إلى فريق IRIS لمتابعة هذا الطلب. ولا يُستخدم كشهادة علنية.',
  'cta.privacyLink': 'كيف نستخدم هذا النموذج',
  'cta.next': 'يردّ فريق IRIS بالبريد خلال 24 ساعة في أيام العمل. البريد فقط. وليس هناك التزام بالشراء.',
  'cta.hint.company': 'المؤسسة التي تستضيف التجربة أو العرض.',
  'cta.hint.email': 'استخدموا عنوان عمل. يردّ فريق IRIS هنا.',
  'cta.hp': 'اتركوا هذا الحقل فارغاً',
  'cta.site.residential_compound': 'مجمّع سكني',
  'cta.site.hotel': 'فندق',
  'cta.site.mall': 'مركز تجاري أو تجزئة',
  'cta.site.hospital': 'مستشفى',
  'cta.site.office': 'مكتب أو مجمّع',
  'cta.site.factory': 'مستودع أو مصنع أو ساحة صناعية',
  'cta.site.city': 'حي مدينة أو شوارع أو أماكن عامة',
  'cta.site.other': 'أخرى (مطار، ميناء…)',

  'faq.eyebrow': 'أسئلة',
  'faq.title': 'قبل أن تطلبوا عرضاً تجريبياً',
  'faq.q1': 'هل العرض التجريبي تجربة مجانية؟',
  'faq.a1':
    'نعم، للخطّين، لمدة أسبوعين إلى أربعة أسابيع. Iris Waste: يركّب IRIS جهازاً مكتملاً على حاوية واحدة تختارونها. Iris Vision: يركّب IRIS خادماً محلياً في موقعكم؛ وتذهب تغذية الكاميرات إلى ذلك الخادم وتبقى عندكم.',
  'faq.q2': 'هل يمكننا تشغيل الخطّين؟',
  'faq.a2': 'نعم. اختاروا «كلاهما» في النموذج. ويستخدم فريقكم تسجيلاً واحداً للخطّين.',
  'faq.q3': 'أين يعمل IRIS؟',
  'faq.a3': 'يمكن لـ IRIS استضافته لكم، أو تشغيله داخل مؤسستكم. ويعمل IRIS في منطقة EMEA. ويجهّز فريق IRIS أياً منهما معكم.',
  'faq.q4': 'هل العربية مدعومة؟',
  'faq.a4': 'نعم. IRIS وهذا الموقع بالعربية والإنجليزية، مع تخطيط من اليمين إلى اليسار.',
  'faq.q5': 'هل يعمل IRIS مع أنظمتنا الأخرى؟',
  'faq.a5': 'يمكن أن تصل التقارير والأعداد والتنبيهات إلى الأنظمة التي يستخدمها فريقكم، مثل SAP أو Oracle أو Power BI.',
  'faq.q6': 'هل الأسعار مذكورة هنا؟',
  'faq.a6': 'لا. اطلبوا عرضاً تجريبياً. وتُناقش الأسعار بعد تحديد الموقع.',

  'legal.updated': 'تصف هذه الصفحة الموقع كما يعمل اليوم. وليست سياسة قانونية موقّعة.',
  'privacy.eyebrow': 'الخصوصية',
  'privacy.title': 'كيف نستخدم نموذج العرض التجريبي.',
  'privacy.p1':
    'يجمع نموذج العرض اسم الشركة واسم جهة الاتصال والبريد المهني والهاتف الاختياري ونوع الموقع والخط الذي يجب أن يغطيه هذا العرض وملاحظات عن الحاوية أو الكاميرات. ويمكنكم مراسلة فريق IRIS بدلاً من استخدام النموذج.',
  'privacy.p2': 'يستخدم فريق IRIS تلك البيانات فقط لمتابعة الطلب. ولا تُستخدم كشهادة علنية.',
  'privacy.p3':
    'قد تُحفظ أفضلية اللغة في متصفحكم إذا بدّلتم بين العربية والإنجليزية. والنموذج نفسه لا يضع ملفات تعريف إعلانية.',
  'privacy.p4':
    'من المسؤول عن البيانات، ومدة حفظ الطلب، وأين يُخزَّن، غير مذكورة هنا. وتحتاج إلى إشعار خصوصية موقَّع. وحتى ذلك الحين، اعتبروا هذه الصفحة وصفاً للنموذج.',
  'privacy.p5':
    'يردّ فريق IRIS بالبريد خلال 24 ساعة في أيام العمل، على العنوان المهني الذي تدخلونه. البريد فقط. أسئلة عن طلب أرسلتموه: استخدموا السلسلة نفسها، أو عنوان المبيعات في التذييل.',

  'terms.eyebrow': 'الشروط',
  'terms.title': 'ماذا يعني طلب عرض تجريبي.',
  'terms.p1': 'إرسال النموذج طلب لترتيب عرض تشغيلي. وليس عقداً لشراء IRIS، ولا يحدّد سعراً أو مستوى خدمة.',
  'terms.p2':
    'Iris Waste: التجربة المجانية تعني أن IRIS يركّب جهازاً مكتملاً على حاوية واحدة في موقع تختارونه، لمدة أسبوعين إلى أربعة أسابيع. وبعد التجربة يُستعاد الجهاز.',
  'terms.p3':
    'Iris Vision: عرض مجاني لمدة أسبوعين إلى أربعة أسابيع. يركّب IRIS خادماً محلياً في موقعكم. وتذهب تغذية الكاميرات إلى ذلك الخادم. ولا تغادر البيانات خوادمكم. وبعد العرض تُحذف بيانات Vision. وأي الكاميرات تعمل يُؤكَّد بعد معاينة الموقع.',
  'terms.p4':
    'خريطة Iris Waste والتنبيهات والتقارير والإعداد هي ما تستخدمونه في العرض. وتخطيط المسارات وتطبيق الطاقم وأزمنة المتعاقد والكربون والتحويل والعرض عبر العقارات وتقارير السكان عبر QR متاحة عند الطلب. وليست جزءاً من العرض.',
  'terms.p5': 'Solace Technologies هو الشريك التقني المذكور في التذييل. والعمل المواجه للعميل في هذا الموقع يقوم به IRIS.',

  'foot.place': '125 Banafseg، القاهرة الجديدة، مصر. EMEA.',
  'foot.sales': 'المبيعات',
  'foot.top': 'العودة إلى الأعلى',
  'foot.partner': 'الشريك التقني: Solace Technologies',

  'shot.waste.operator-alerts.laptop':
    'صندوق تنبيهات Iris Waste مع أعداد ملخصة وقائمة تحذيرات لحاويات ستفيض قبل الجمع، لكل منها إجراءات إقرار وإغلاق.',
  'shot.waste.operator-bin.phone':
    'شاشة Iris Waste على الهاتف لحاوية شارع في حالة تحذير، متوقع امتلاؤها خلال ساعات، مع وقت الجمع التالي ومخطط سجل الامتلاء.',
  'shot.waste.executive-view.laptop':
    'تقارير Iris Waste مع متوسط الامتلاء عند الجمع كرقم رئيسي، إضافة إلى الحاويات المراقبة والفيضانات المتجنَّبة وعمليات الجمع هذا الأسبوع والجمع ونصفها فارغ.',
  'shot.waste.executive-view.phone':
    'تقارير Iris Waste على الهاتف: متوسط الامتلاء عند الجمع خلال آخر 30 يوماً مقابل هدف 75٪، مع الحاويات المراقبة والجمع ونصفها فارغ.',
  'shot.waste.installer-activation-code.phone':
    'خطوة تركيب Iris Waste على الهاتف مع رمز تفعيل لمرة واحدة وعدّ تنازلي 15 دقيقة ورمز QR لمسحه على الجهاز.',
  'shot.waste.tenant-admin-groups.laptop':
    'صفحة مجموعات Iris Waste تُظهر الإعدادات وهي تنتقل من المؤسسة إلى الموقع والمجموعة والحاوية، مع ست مجموعات حاويات وقواعد التحذير والحرج والجمع.',
  'shot.waste.map.laptop':
    'خريطة الموقع في Iris Waste لمجمّع Garden Residences: حاويات الشارع كحلقات امتلاء على الخريطة، وملخص للأسطول، ولوحة مفتوحة لحاوية Olive Street 4 متوقع فيضانها قبل الجمع. بيانات نموذجية.',
  'shot.waste.routes.laptop':
    'صفحة ترتيب الجمع في Iris Waste مع محطات اليوم بالترتيب وامتلاء كل حاوية ووقت الوصول والمسار مرسوماً على خريطة. بيانات نموذجية. متاح عند الطلب.',
  'shot.waste.crew.phone':
    'تطبيق طاقم Iris Waste على الهاتف عند المحطة 4 من 12: حاوية في الردهة بامتلاء 79٪، وزر لمسح رمز QR للحاوية، وخيارات لإضافة صورة أو الإبلاغ عن مشكلة. بيانات نموذجية. متاح عند الطلب.',
  'shot.vision.alerts.laptop':
    'صندوق تنبيهات Iris Vision مع تنبيهات نموذجية لكل منها لقطة من الكاميرا، وتنبيه مفتوح يُظهر شخصاً في مسار رافعة شوكية معلَّماً على اللقطة.',
  'shot.vision.alert.phone':
    'تنبيه Iris Vision على الهاتف: شخص في مسار رافعة شوكية، مع اللقطة والكاميرا والمنطقة والوقت، وزرّي الإقرار والإسناد.',
  'shot.vision.cameras.laptop':
    'شبكة كاميرات Iris Vision مع ست لقطات ورصدها معلَّماً: عدّ عند مدخل متجر، وطابور عند المحاسبة، ومخرج طوارئ مسدود، ومسار رافعة شوكية، وأرصفة تحميل، ودخول خلف شخص آخر عند بوابات الردهة.',
  'shot.vision.counts.laptop':
    'صفحة الأعداد والإشغال في Iris Vision مع بيانات نموذجية لعدد الزوار بالساعة ومدة البقاء حسب المنطقة وأشرطة الإشغال لكل منطقة ومدخل متجر عليه خط عدّ.',
  'shot.vision.counts.phone':
    'الأعداد في Iris Vision على الهاتف: عدد الزوار حتى الآن، وتنبيه طابور عند المحاسبة، والإشغال حسب المنطقة. بيانات نموذجية.',
  'shot.vision.zones.laptop':
    'إعداد المناطق والقواعد في Iris Vision: مسار رافعة شوكية مرسوم على منظر كاميرا مستودع، مع قاعدة للأشخاص في المسار وفحص الخوذة والسترة ودرجة الخطورة ومن يُبلَّغ.',
  'shot.vision.report.laptop':
    'التقرير اليومي في Iris Vision مع بيانات نموذجية للتنبيهات حسب النوع وزمن الاستجابة وعدد الزوار وأحداث السلامة، ولقطات للأحداث التي تستحق النظر، وأزرار التصدير.',
};

const dict: Record<Lang, Dict> = { en, ar };

export function t(lang: Lang, key: string, vars?: Record<string, string | number>): string {
  const raw = dict[lang][key] ?? dict.en[key] ?? key;
  if (!vars) return raw;
  return raw.replace(/\{(\w+)\}/g, (_, k: string) => String(vars[k] ?? `{${k}}`));
}

export function persistLang(lang: Lang) {
  try {
    localStorage.setItem('iris-landing-lang', lang);
  } catch {
    /* storage can be blocked */
  }
}

export function readLang(): Lang {
  if (typeof window !== 'undefined') {
    const { lang } = stripPathLang(window.location.pathname);
    if (lang) return lang;
    const q = new URLSearchParams(window.location.search).get('lang');
    if (q === 'ar' || q === 'en') return q;
  }
  return 'en';
}

function stripPathLang(pathname: string): { lang: Lang | null } {
  const clean = pathname.replace(/\/index\.html$/, '').replace(/\/+$/, '') || '/';
  if (clean === '/ar' || clean.startsWith('/ar/')) return { lang: 'ar' };
  return { lang: 'en' };
}

export function missingTranslations(): string[] {
  const missing = Object.keys(en).filter((k) => !(k in ar));
  const extra = Object.keys(ar).filter((k) => !(k in en)).map((k) => `extra:${k}`);
  return [...missing, ...extra];
}

export const FAQ_COUNT = 6;

export function faqEntries(lang: Lang, prefix = 'faq', count = FAQ_COUNT): { q: string; a: string }[] {
  return Array.from({ length: count }, (_, i) => ({
    q: t(lang, `${prefix}.q${i + 1}`),
    a: t(lang, `${prefix}.a${i + 1}`),
  }));
}
